import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { AlertTriangle, ArrowRightLeft, CheckCircle2, CreditCard, Pencil, ReceiptText, RotateCcw, Send, SendToBack, Trash2 } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getRole } from "../actions/userActions";
import * as XLSX from "xlsx";

const API = process.env.REACT_APP_API_URL;
const API_ORIGIN = (API || "").replace(/\/api\/?$/, "");
const inputCls =
  "border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white";
const FAILED_ROWS_PER_PAGE = 10;

const IMPORT_FIELDS = [
  ["merchantName", "Merchant"],
  ["merchantIdentifier", "Identifier"],
  ["leadDate", "Lead Date (D/M/YYYY)"],
  ["disburshMonth", "Disbursh Month"],
  ["caseName", "Case Name"],
  ["mobileNumber", "Mobile Number"],
  ["referredBy", "Ref.by"],
  ["loanType", "Loan type"],
  ["amountRequired", "Amount require"],
  ["loginFrom", "Login from"],
  ["status", "Status"],
  ["remarks", "Remarks"],
  ["workableStatus", "Workable/ Notworkable"],
  ["feLoginAmount", "Fe Login Amount"],
  ["feDisbursementAmount", "Fe Disburshment Amount"],
  ["bankPayments", "Bank payments"],
  ["loginFeesStatus", "Login Fees Status"],
  ["nextMonthFollowup", "For next month Followup"],
];

const ACCEPTED_IMPORT_STATUSES = [
  "Lead Search",
  "Document Recives",
  "Login(Bank)",
  "Visits from Bank",
  "Sanction of Amount",
  "Documents Signeal",
  "Disburse",
  "Under Follow up",
  "Reject",
  "Deal Cancel",
  "Disbursement",
  "Login",
  "Not Applicable",
  "Rejected",
  "Under Follow Up",
];

const IMPORT_STATUS_OPTIONS = [
  "Deal Cancel",
  "Disbursement",
  "Login",
  "Not Applicable",
  "Rejected",
  "Under Follow Up",
];

const EDIT_STATUS_OPTIONS = [
  "Deal Cancel",
  "Disbursement",
  "Login",
  "Not Applicable",
  "Rejected",
  "Under Follow Up",
];

const isNotAvailableValue = (value) => /^(?:n\s*\/\s*a|na)$/i.test(String(value ?? "").trim());

const statusKey = (value) => String(value ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");

const editDistance = (left, right) => {
  const previous = Array.from({ length: right.length + 1 }, (_, index) => index);
  for (let leftIndex = 1; leftIndex <= left.length; leftIndex += 1) {
    let diagonal = previous[0];
    previous[0] = leftIndex;
    for (let rightIndex = 1; rightIndex <= right.length; rightIndex += 1) {
      const above = previous[rightIndex];
      previous[rightIndex] = Math.min(
        previous[rightIndex] + 1,
        previous[rightIndex - 1] + 1,
        diagonal + (left[leftIndex - 1] === right[rightIndex - 1] ? 0 : 1),
      );
      diagonal = above;
    }
  }
  return previous[right.length];
};

const suggestedImportStatus = (value) => {
  const key = statusKey(value);
  if (!key) return "";
  const closest = IMPORT_STATUS_OPTIONS
    .map((status) => ({ status, distance: editDistance(key, statusKey(status)) }))
    .sort((left, right) => left.distance - right.distance)[0];
  return closest && closest.distance <= Math.max(2, Math.floor(key.length * 0.3))
    ? closest.status
    : "";
};

const importDateValue = (value) => {
  if (!value) return "";
  const text = String(value).trim();
  const isoDate = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoDate) return `${isoDate[3]}/${isoDate[2]}/${isoDate[1]}`;

  const namedDate = text.match(/^(\d{1,2})-([A-Za-z]{3,9})-(\d{2,4})$/);
  if (!namedDate) return text;
  const monthNames = [
    "jan", "feb", "mar", "apr", "may", "jun",
    "jul", "aug", "sep", "oct", "nov", "dec",
  ];
  const month = monthNames.indexOf(namedDate[2].slice(0, 3).toLowerCase()) + 1;
  if (!month) return text;
  const year = namedDate[3].length === 2 ? `20${namedDate[3]}` : namedDate[3];
  return `${namedDate[1].padStart(2, "0")}/${String(month).padStart(2, "0")}/${year}`;
};

const sanitizePhoneNumbers = (value) => {
  const cleaned = String(value || "").replace(/[^\d/]/g, "");
  const slashIndex = cleaned.indexOf("/");
  const first = (slashIndex === -1 ? cleaned : cleaned.slice(0, slashIndex)).slice(0, 10);
  if (slashIndex === -1 || first.length < 10) return first;
  return `${first}/${cleaned.slice(slashIndex + 1).replace(/\//g, "").slice(0, 10)}`;
};

const validateImportField = (field, value) => {
  const text = String(value ?? "").trim();
  if (isNotAvailableValue(text)) return "";
  if (field === "caseName" && !text) return "Case name is required";
  if (field === "merchantIdentifier" && !text) return "Identifier is required";
  if (field === "mobileNumber") {
    return !text || /^\d{10}(?:\/\d{10})?$/.test(text)
      ? ""
      : "Enter 10 digits, or two 10-digit numbers separated by /";
  }
  if (field === "leadDate" && text) {
    const match = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!match) return "Lead date must be D/M/YYYY (day/month/year)";
    const [, day, month, year] = match;
    const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
    if (
      date.getUTCFullYear() !== Number(year) ||
      date.getUTCMonth() !== Number(month) - 1 ||
      date.getUTCDate() !== Number(day)
    ) {
      return "Lead date must be a valid day/month/year date";
    }
  }
  if (field === "status" && text) {
    const normalized = text.toLowerCase().replace(/\s+/g, " ");
    const valid = ACCEPTED_IMPORT_STATUSES.some(
      (status) => status.toLowerCase().replace(/\s+/g, " ") === normalized,
    );
    if (!valid) return "Invalid status. Check the spelling";
  }
  return "";
};

const COLUMNS = [
  { key: "select", label: "" },
  { key: "serial", label: "S.No" },
  { key: "leadDate", label: "Lead Date", type: "date" },
  { key: "caseName", label: "Case Name" },
  { key: "mobileNumber", label: "Mobile Number" },
  { key: "amountRequired", label: "Amount Required", type: "amount" },
  { key: "loanType", label: "Loan Type" },
  { key: "merchantName", label: "Merchant Name" },
  { key: "merchantIdentifier", label: "Identifier" },
  { key: "documentsDate", label: "Documents Date", type: "date" },
  { key: "loginDate", label: "Login Date", type: "date" },
  { key: "disburshMonth", label: "Disbursh Month", type: "month" },
  { key: "referredBy", label: "Ref.by" },
  { key: "loginFrom", label: "Login From" },
  { key: "status", label: "Status" },
  { key: "transferDetails", label: "Transfer Details" },
  { key: "remarks", label: "Remarks" },
  { key: "workableStatus", label: "Workable / Notworkable" },
  { key: "feLoginAmount", label: "Fe Login Amount", type: "amount" },
  {
    key: "feDisbursementAmount",
    label: "Fe Disburshment Amount",
    type: "amount",
  },
  { key: "bankPayments", label: "Bank Payments", type: "amount" },
  { key: "loginFeesStatus", label: "Login Fees Status" },
  { key: "nextMonthFollowup", label: "For next month Followup" },
  { key: "aadhaarImage", label: "Aadhaar Front", type: "document" },
  { key: "aadhaarBackImage", label: "Aadhaar Back", type: "document" },
  { key: "panCardImage", label: "PAN Card", type: "document" },
  { key: "bankStatementImage", label: "Bank Statement", type: "document" },
  { key: "additionalDocumentType", label: "Other Doc Type" },
  { key: "additionalDocumentImage", label: "Other Document", type: "document" },
  { key: "welcomeCallMessage", label: "Welcome Call/Message" },
  { key: "sentBackTag", label: "Sent Back" },
  { key: "actions", label: "Action" },
];

const MERCHANT_COLUMNS = [
  { key: "serial", label: "S.No" },
  { key: "paymentLedger", label: "Payment Ledger" },
  { key: "sentBackTag", label: "Send Back" },
  { key: "caseName", label: "Case Name" },
  { key: "mobileNumber", label: "Mobile Number" },
  { key: "referredBy", label: "Ref.by" },
  { key: "loanType", label: "Loan Type" },
  { key: "amountRequired", label: "Amount Required", type: "amount" },
  { key: "status", label: "Status" },
  { key: "transferDetails", label: "Transfer Details" },
  { key: "loginFeesStatus", label: "Login Fees Status" },
  { key: "welcomeCallMessage", label: "Welcome Call/Message" },
  { key: "remarks", label: "Remarks" },
  { key: "paidAmount", label: "Paid Amount", type: "amount" },
  { key: "aadhaarImage", label: "Aadhaar Front", type: "document" },
  { key: "aadhaarBackImage", label: "Aadhaar Back", type: "document" },
  { key: "panCardImage", label: "PAN Card", type: "document" },
  { key: "bankStatementImage", label: "Bank Statement", type: "document" },
  { key: "additionalDocumentType", label: "Other Doc Type" },
  { key: "additionalDocumentImage", label: "Other Document", type: "document" },
  { key: "actions", label: "Action" },
];

const SEND_BACK_COLUMNS = [
  { key: "serial", label: "S.No" },
  { key: "merchantName", label: "Merchant Name" },
  { key: "merchantIdentifier", label: "Identifier" },
  { key: "caseName", label: "Case Name" },
  { key: "mobileNumber", label: "Mobile Number" },
  { key: "referredBy", label: "Ref.by" },
  { key: "loanType", label: "Loan Type" },
  { key: "amountRequired", label: "Amount Required", type: "amount" },
  { key: "status", label: "Status" },
  { key: "transferDetails", label: "Transfer Details" },
  { key: "loginFeesStatus", label: "Login Fees Status" },
  { key: "welcomeCallMessage", label: "Welcome Call/Message" },
  { key: "remarks", label: "Remarks" },
  { key: "aadhaarImage", label: "Aadhaar Front", type: "document" },
  { key: "aadhaarBackImage", label: "Aadhaar Back", type: "document" },
  { key: "panCardImage", label: "PAN Card", type: "document" },
  { key: "bankStatementImage", label: "Bank Statement", type: "document" },
  { key: "additionalDocumentType", label: "Other Doc Type" },
  { key: "additionalDocumentImage", label: "Other Document", type: "document" },
  { key: "actions", label: "Action" },
];

const emptyEditForm = {
  merchantName: "",
  merchantIdentifier: "",
  documentsDate: "",
  loginDate: "",
  disburshMonth: "",
  caseName: "",
  mobileNumber: "",
  referredBy: "",
  loanType: "",
  amountRequired: "",
  loginFrom: "",
  status: "",
  sendBackReason: "",
  remarks: "",
  workableStatus: "",
  feLoginAmount: "",
  feDisbursementAmount: "",
  bankPayments: "",
  loginFeesStatus: "",
  additionalDocumentType: "",
};

const emptyEditFiles = {
  aadhaarImage: null,
  aadhaarBackImage: null,
  panCardImage: null,
  bankStatementImage: null,
  additionalDocumentImage: null,
};

const dateInputValue = (value) =>
  value ? dayjs(value).format("YYYY-MM-DD") : "";

const monthInputValue = (value) => {
  if (!value) return "";
  const text = String(value).trim();
  const inputMonth = text.match(/^(\d{4})-(\d{1,2})$/);
  if (inputMonth) return `${inputMonth[1]}-${inputMonth[2].padStart(2, "0")}`;

  const monthYear = text.match(/^([A-Za-z]{3,9})[-\s/](\d{2,4})$/);
  if (monthYear) {
    const year = monthYear[2].length === 2 ? `20${monthYear[2]}` : monthYear[2];
    const parsed = dayjs(`01 ${monthYear[1].slice(0, 3)} ${year}`);
    return parsed.isValid() ? parsed.format("YYYY-MM") : "";
  }

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("YYYY-MM") : "";
};

const getEditFormValues = (row) => ({
  merchantName: row?.merchantName || row?.merchant?.name || "",
  merchantIdentifier: row?.merchantIdentifier || row?.merchant?.identifier || "",
  documentsDate: dateInputValue(row?.documentsDate),
  loginDate: dateInputValue(row?.loginDate),
  disburshMonth: monthInputValue(row?.disburshMonth),
  caseName: row?.caseName || "",
  mobileNumber: row?.mobileNumber || "",
  referredBy: row?.referredBy || "",
  loanType: row?.loanType || "",
  amountRequired: row?.amountRequired ?? "",
  loginFrom: row?.loginFrom || "",
  status: row?.status || "",
  sendBackReason:
    row?.sendBackReason || row?.rejectReason || row?.rejectionReason || "",
  remarks: row?.remarks || "",
  workableStatus: row?.workableStatus || "",
  feLoginAmount: row?.feLoginAmount ?? "",
  feDisbursementAmount: row?.feDisbursementAmount ?? "",
  bankPayments: row?.bankPayments ?? "",
  loginFeesStatus: row?.loginFeesStatus || "",
  additionalDocumentType: row?.additionalDocumentType || "",
});

const getDocumentUrl = (value) => {
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  return `${API_ORIGIN}${value.startsWith("/") ? value : `/${value}`}`;
};

const formatMonth = (value) => {
  if (!value) return "-";

  const text = String(value).trim();
  const monthYear = text.match(/^([A-Za-z]{3,9})[-\s/](\d{2,4})$/);
  if (monthYear) {
    const year = monthYear[2].length === 2 ? `20${monthYear[2]}` : monthYear[2];
    const date = dayjs(`01 ${monthYear[1].slice(0, 3)} ${year}`);
    return date.isValid() ? date.format("MMM-YY") : text;
  }

  const inputMonth = text.match(/^(\d{4})-(\d{1,2})$/);
  if (inputMonth) {
    const date = dayjs(`${inputMonth[1]}-${inputMonth[2].padStart(2, "0")}-01`);
    return date.isValid() ? date.format("MMM-YY") : text;
  }

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("MMM-YY") : text;
};

const getPaymentAmounts = (row) => {
  const due = {
    lead: Number(row?.approvedAmount) || 0,
    login: Number(row?.feLoginAmount) || 0,
    disbursement: Number(row?.feDisbursementAmount) || 0,
  };
  const paid = {
    lead: Number(row?.paymentLeadAmount) || 0,
    login: Number(row?.paymentLoginAmount) || 0,
    disbursement: Number(row?.paymentDisbursementAmount) || 0,
  };
  const outstanding = {
    lead: Math.max(due.lead - paid.lead, 0),
    login: Math.max(due.login - paid.login, 0),
    disbursement: Math.max(due.disbursement - paid.disbursement, 0),
  };
  return {
    due,
    paid,
    outstanding,
    outstandingTotal: outstanding.lead + outstanding.login + outstanding.disbursement,
  };
};

const getPaymentStatus = (row) => {
  const { due, paid } = getPaymentAmounts(row);

  if (row?.paid === true) return "full";
  if (due.lead <= 0) return "none";
  if (paid.lead < due.lead) return "pending";
  return "partial";
};

const getPaymentLedger = (row) => {
  const entries = Array.isArray(row?.paymentLedger) ? row.paymentLedger : [];
  if (entries.length > 0) return entries;
  if (!row?.paymentProofImage) return [];
  return [
    {
      leadAmount: row.paymentLeadAmount || 0,
      loginAmount: row.paymentLoginAmount || 0,
      disbursementAmount: row.paymentDisbursementAmount || 0,
      totalAmount: row.paidAmount || 0,
      proofImage: row.paymentProofImage,
      paidAt: row.lastPaymentAt || row.paidAt,
    },
  ];
};

const formatCell = (row, col, index, page, limit, serialSort, pageItemCount) => {
  if (col.key === "serial")
    return serialSort === "desc"
      ? (page - 1) * limit + pageItemCount - index
      : (page - 1) * limit + index + 1;
  if (col.key === "merchantName")
    return row.merchantName || row.merchant?.name || "—";
  if (col.key === "merchantIdentifier")
    return row.merchantIdentifier || row.merchant?.identifier || "—";
  const value = row[col.key];
  if (col.type === "month") return formatMonth(value);
  if (col.type === "date")
    return value ? dayjs(value).format("DD MMM YYYY") : "—";
  if (col.type === "amount")
    return value === 0 || value ? Number(value).toLocaleString("en-IN") : "—";
  if (col.key === "workableStatus") {
    if (value === "workable") return "Workable";
    if (value === "not_workable") return "Notworkable";
  }
  return value || "—";
};

const getTransferDetails = (row) => {
  if (!row?.transferred && row?.status !== "Transferred") return null;
  const transferredAt = row?.transferredAt
    ? dayjs(row.transferredAt).format("DD MMM YYYY, hh:mm A")
    : "";
  return {
    by: row?.transferredByName || row?.transferredBy?.name || "—",
    byIdentifier: row?.transferredByIdentifier || "",
    at: transferredAt || "—",
    from:
      row?.transferFromMerchantName ||
      row?.transferFromMerchant?.name ||
      "—",
    fromIdentifier: row?.transferFromMerchantIdentifier || "",
    to:
      row?.transferToMerchantName ||
      row?.transferToMerchant?.name ||
      row?.merchantName ||
      row?.merchant?.name ||
      "—",
    toIdentifier:
      row?.transferToMerchantIdentifier ||
      row?.merchantIdentifier ||
      row?.merchant?.identifier ||
      "",
  };
};

const renderCell = (
  row,
  col,
  index,
  page,
  limit,
  onViewPaymentLedger,
  serialSort,
  pageItemCount,
) => {
  if (col.key === "welcomeCallMessage") return row.welcomeCallMessage ? "Yes" : "No";
  if (col.key === "paid") {
    const paymentStatus = getPaymentStatus(row);
    const paymentLabel = {
      none: "—",
      pending: "Payment Pending",
      partial: "Partial Payment",
      full: "Full Payment",
    }[paymentStatus];
    return (
      <div className="flex items-center gap-2">
        <span
          className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${
            paymentStatus === "full"
              ? "border-green-200 bg-green-50 text-green-700"
              : paymentStatus === "partial"
                ? "border-blue-200 bg-blue-50 text-blue-700"
                : paymentStatus === "pending"
                  ? "border-orange-200 bg-orange-50 text-orange-700"
                  : "border-gray-200 bg-gray-50 text-gray-500"
          }`}
        >
          {paymentLabel}
        </span>
        {row.paymentProofImage && (
          <a
            href={getDocumentUrl(row.paymentProofImage)}
            target="_blank"
            rel="noreferrer"
            className="text-xs font-semibold text-blue-600 hover:text-blue-800"
          >
            View proof
          </a>
        )}
      </div>
    );
  }
  if (col.key === "paymentLedger") {
    const ledger = getPaymentLedger(row);
    if (ledger.length === 0) return "—";
    return (
      <button
        type="button"
        onClick={() => onViewPaymentLedger(row)}
        className="inline-flex items-center gap-1.5 rounded-lg border border-purple-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-purple-700 transition-colors hover:border-purple-400 hover:bg-purple-50"
      >
        <ReceiptText size={14} strokeWidth={2.2} />
        View Ledger ({ledger.length})
      </button>
    );
  }
  if (col.key === "status") {
    const statusText =
      row.status === "Transferred"
        ? row.transferPreviousStatus || "—"
        : formatCell(row, col, index, page, limit);
    const rejectReason = row.rejectReason || row.rejectionReason || row.sendBackReason;

    if (["Reject", "Rejected"].includes(row.status) && rejectReason) {
      return (
        <div className="max-w-[240px] whitespace-normal">
          <p className="font-medium text-red-600">{statusText}</p>
          <p className="mt-0.5 text-xs text-gray-500 break-words">
            {rejectReason}
          </p>
        </div>
      );
    }

    return statusText;
  }

  if (col.key === "transferDetails") {
    const transferDetails = getTransferDetails(row);
    if (!transferDetails) return "—";
    return (
      <div className="min-w-[240px] max-w-[300px] text-xs leading-5 text-gray-600">
        <p>
          <span className="font-semibold text-gray-800">By:</span>{" "}
          {transferDetails.by}
          {transferDetails.byIdentifier ? ` (${transferDetails.byIdentifier})` : ""}
        </p>
        <p>
          <span className="font-semibold text-gray-800">At:</span>{" "}
          {transferDetails.at}
        </p>
        <p>
          <span className="font-semibold text-gray-800">From:</span>{" "}
          {transferDetails.from}
          {transferDetails.fromIdentifier ? ` (${transferDetails.fromIdentifier})` : ""}
        </p>
        <p>
          <span className="font-semibold text-gray-800">To:</span>{" "}
          {transferDetails.to}
          {transferDetails.toIdentifier ? ` (${transferDetails.toIdentifier})` : ""}
        </p>
      </div>
    );
  }

  if (col.key === "sentBackTag") {
    const count = Number(row.sendBackCount || 0) || (row.wasSentBack ? 1 : 0);
    return count > 0 ? (
      <span className="inline-flex items-center rounded-full border border-orange-200 bg-orange-50 px-2.5 py-1 text-xs font-semibold text-orange-700">
        Sent Back ({count})
      </span>
    ) : (
      "-"
    );
  }

  if (col.type === "document") {
    const url = getDocumentUrl(row[col.key]);
    if (!url) return "—";
    return (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="text-blue-600 hover:text-blue-800 text-xs font-medium border border-blue-200 hover:border-blue-400 px-3 py-1 rounded-lg transition-colors"
      >
        View
      </a>
    );
  }

  return formatCell(row, col, index, page, limit, serialSort, pageItemCount);
};

const exportCell = (row, col, index) => {
  if (col.key === "welcomeCallMessage") return row.welcomeCallMessage ? "Yes" : "No";
  if (col.key === "paid") {
    return {
      none: "",
      pending: "Payment Pending",
      partial: "Partial Payment",
      full: "Full Payment",
    }[getPaymentStatus(row)];
  }
  if (col.key === "sentBackTag") {
    const count = Number(row.sendBackCount || 0) || (row.wasSentBack ? 1 : 0);
    return count > 0 ? `Sent Back (${count})` : "";
  }
  if (col.type === "document") return getDocumentUrl(row[col.key]) || "";
  if (col.key === "transferDetails") {
    const transferDetails = getTransferDetails(row);
    if (!transferDetails) return "";
    return [
      `By: ${transferDetails.by}${transferDetails.byIdentifier ? ` (${transferDetails.byIdentifier})` : ""}`,
      `At: ${transferDetails.at}`,
      `From: ${transferDetails.from}${transferDetails.fromIdentifier ? ` (${transferDetails.fromIdentifier})` : ""}`,
      `To: ${transferDetails.to}${transferDetails.toIdentifier ? ` (${transferDetails.toIdentifier})` : ""}`,
    ].join(" | ");
  }
  if (col.type === "amount") {
    const value = row[col.key];
    return value === 0 || value ? Number(value) : "";
  }
  return formatCell(row, col, index, 1, 1);
};

function Modal({
  open,
  title,
  children,
  footer,
  onClose,
  maxWidth = "max-w-5xl",
  bodyClassName = "overflow-y-auto",
}) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-4"
      onClick={onClose}
    >
      <div
        className={`bg-white rounded-xl shadow-xl w-full ${maxWidth} max-h-[92vh] flex flex-col`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 px-4 py-3 sm:px-6 sm:py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            &times;
          </button>
        </div>
        <div className={`px-4 py-4 sm:px-6 flex-1 ${bodyClassName}`}>
          {children}
        </div>
        {footer && (
          <div className="flex flex-col-reverse gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-end sm:gap-3 sm:px-6 sm:py-4 border-t border-gray-200">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

function InlineDropdown({ value, options, onChange, ariaLabel, widthClass = "sm:w-auto" }) {
  const [open, setOpen] = useState(false);
  const selected =
    options.find((option) => option.value === value) || options[0];

  return (
    <div
      className={`relative w-full min-w-0 max-w-full ${widthClass}`}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false);
      }}
    >
      <button
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        className={`${inputCls} flex w-full min-w-0 max-w-full items-center justify-between gap-3 text-left`}
      >
        <span className="truncate">{selected?.label}</span>
        <span className="text-xs text-gray-400">▾</span>
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute left-0 right-0 top-full z-40 mt-1 max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg sm:min-w-full"
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={option.value === value}
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
              className={`block w-full px-3 py-2 text-left text-sm hover:bg-blue-50 ${option.value === value ? "bg-blue-50 text-blue-700" : "text-gray-700"}`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function MonthMultiSelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const label = value.length === 0
    ? "Disbursh Month"
    : value.length === 12
      ? "All 12 Months"
      : `${value.length} Month${value.length === 1 ? "" : "s"}`;

  const toggleMonth = (month) => {
    onChange(
      value.includes(month)
        ? value.filter((item) => item !== month)
        : [...value, month],
    );
  };

  return (
    <div
      className="relative w-full sm:w-auto"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false);
      }}
    >
      <button
        type="button"
        aria-label="Filter by disbursh month"
        aria-expanded={open}
        onClick={() => setOpen((previous) => !previous)}
        className={`${inputCls} flex w-full items-center justify-between gap-3 text-left sm:w-auto`}
      >
        <span>{label}</span>
        <span className="text-xs text-gray-400">▾</span>
      </button>
      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-52 rounded-lg border border-gray-200 bg-white p-2 shadow-lg">
          <div className="mb-2 flex gap-2 border-b border-gray-100 pb-2">
            <button type="button" onClick={() => onChange(months)} className="text-xs font-medium text-blue-600 hover:text-blue-800">
              Select all
            </button>
            <button type="button" onClick={() => onChange([])} className="text-xs font-medium text-gray-500 hover:text-gray-700">
              Clear
            </button>
          </div>
          <div className="grid grid-cols-3 gap-1">
            {months.map((month) => (
              <label key={month} className="flex cursor-pointer items-center gap-1.5 rounded px-1.5 py-1.5 text-sm hover:bg-blue-50">
                <input type="checkbox" checked={value.includes(month)} onChange={() => toggleMonth(month)} />
                {month}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function LoanList({
  merchantId = "",
  sendBack = false,
  title = "",
}) {
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.userLogin || {});
  const [rejectModal, setRejectModal] = useState({
    open: false,
    row: null,
    reason: "",
  });
  const [approveModal, setApproveModal] = useState({
    open: false,
    row: null,
  });
  const [welcomeConfirmModal, setWelcomeConfirmModal] = useState({
    open: false,
    row: null,
  });
  const [paymentModal, setPaymentModal] = useState({
    open: false,
    row: null,
    file: null,
    amounts: { lead: "", login: "", disbursement: "" },
    error: "",
  });
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [ledgerModal, setLedgerModal] = useState({ open: false, row: null });
  const [sendBackModal, setSendBackModal] = useState({
    open: false,
    row: null,
    mode: "",
  });
  const [transferModal, setTransferModal] = useState({
    open: false,
    row: null,
    identifier: "",
    selectedMerchant: null,
    suggestions: [],
    suggestionsOpen: false,
    loading: false,
    saving: false,
    error: "",
  });
  const [merchantIdentifierLoading, setMerchantIdentifierLoading] = useState(false);
  const submitReject = async () => {
    if (!rejectModal.reason.trim()) return;
    setMessage("");
    try {
      await axios.put(
        `${API}/enquiries/${rejectModal.row._id}/reject`,
        { reason: rejectModal.reason },
        authConfig,
      );
      setMessage("Enquiry rejected.");
      setRejectModal({ open: false, row: null, reason: "" });
      await fetchList();
    } catch (err) {
      setMessage(
        err?.response?.data?.message || err.message || "Reject failed",
      );
    }
  };

  const { getRoleData } = useSelector((state) => state.getRoleInfo || {});
  const activeRole = getRoleData || userInfo?.role || {};
  const isMerchantUser =
    activeRole?.name?.toLowerCase() === "merchant" || !!userInfo?.merchantId;
  const effectiveMerchantId =
    merchantId || (isMerchantUser ? userInfo?.merchantId : "");
  const authConfig = {
    headers: userInfo?.token
      ? { Authorization: `Bearer ${userInfo.token}` }
      : {},
  };
  const canViewEnquiries = sendBack
    ? !!activeRole?.sendBackEnquiriesView
    : !!activeRole?.loanEnquiriesView;
  const canEditEnquiry = sendBack
    ? !!activeRole?.sendBackEnquiriesEdit
    : !!activeRole?.loanEnquiriesEdit;
  const canDeleteEnquiry = sendBack
    ? !!activeRole?.sendBackEnquiriesDelete
    : !!activeRole?.loanEnquiriesDelete;
  const canMoveEnquiry = !isMerchantUser && !!activeRole?.loanEnquiriesSendBack;
  const canRestoreEnquiry = !isMerchantUser && !!activeRole?.sendBackEnquiriesRestore;
  const canConfirmWelcomeCall = !isMerchantUser && (sendBack
    ? !!activeRole?.sendBackEnquiriesWelcomeCall
    : canEditEnquiry);
  const canTransferMerchant =
    !isMerchantUser && !sendBack && !!activeRole?.loanEnquiriesTransferMerchant;
  const canApproveEnquiry = !isMerchantUser && !sendBack && !!activeRole?.loanEnquiriesEdit;
  const canEditRow = (row) =>
    row?.isSendBack
      ? !!activeRole?.sendBackEnquiriesEdit
      : !!activeRole?.loanEnquiriesEdit;
  const listColumns = sendBack
    ? isMerchantUser
      ? SEND_BACK_COLUMNS.filter((column) => !["merchantName", "merchantIdentifier"].includes(column.key))
      : SEND_BACK_COLUMNS
    : isMerchantUser
      ? MERCHANT_COLUMNS
      : COLUMNS;

  const visibleColumns = canDeleteEnquiry
    ? listColumns
    : listColumns.filter((column) => column.key !== "select");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [customDateFrom, setCustomDateFrom] = useState("");
  const [customDateTo, setCustomDateTo] = useState("");
  const [selectedDisburshMonths, setSelectedDisburshMonths] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [sentBackFilter, setSentBackFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [serialSort, setSerialSort] = useState("asc");
  const [data, setData] = useState({
    items: [],
    total: 0,
    sentBackTotal: 0,
    pages: 0,
    page: 1,
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [message, setMessage] = useState("");
  const [editingRow, setEditingRow] = useState(null);
  const [editForm, setEditForm] = useState(emptyEditForm);
  const [rejectionReasonError, setRejectionReasonError] = useState(false);
  const [editFiles, setEditFiles] = useState(emptyEditFiles);
  const [savingEdit, setSavingEdit] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [selectedImportFile, setSelectedImportFile] = useState(null);
  const [importPreviewOpen, setImportPreviewOpen] = useState(false);
  const [importPreviewFile, setImportPreviewFile] = useState(null);
  const [importPreviewRows, setImportPreviewRows] = useState([]);
  const [importPreviewPage, setImportPreviewPage] = useState(1);
  const [importParsing, setImportParsing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [uploadResult, setUploadResult] = useState(null);
  const [failedPreviewOpen, setFailedPreviewOpen] = useState(false);
  const [failedDrafts, setFailedDrafts] = useState([]);
  const [failedPreviewPage, setFailedPreviewPage] = useState(1);
  const failedFieldRefs = useRef({});
  const lastJumpedError = useRef(null);
  const connectionInitialized = useRef(false);

  useEffect(() => {
    if (!editingRow || isMerchantUser || sendBack) return;
    const merchantName = String(editForm.merchantName || "").trim();
    if (!merchantName) {
      setEditForm((current) =>
        current.merchantIdentifier ? { ...current, merchantIdentifier: "" } : current,
      );
      return;
    }

    let cancelled = false;
    setMerchantIdentifierLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const { data: merchantData } = await axios.get(`${API}/merchants`, {
          headers: userInfo?.token
            ? { Authorization: `Bearer ${userInfo.token}` }
            : {},
          params: { q: merchantName, page: 1, limit: 20 },
        });
        if (cancelled) return;
        const merchants = merchantData?.items || [];
        const exact = merchants.find(
          (merchant) =>
            String(merchant.name || "").trim().toLowerCase() ===
            merchantName.toLowerCase(),
        );
        const matchedMerchant = exact || merchants[0];
        setEditForm((current) => ({
          ...current,
          merchantIdentifier: matchedMerchant?.identifier || "",
        }));
      } catch {
        if (!cancelled) {
          setEditForm((current) => ({ ...current, merchantIdentifier: "" }));
        }
      } finally {
        if (!cancelled) setMerchantIdentifierLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      setMerchantIdentifierLoading(false);
    };
  }, [editingRow, editForm.merchantName, isMerchantUser, sendBack, userInfo?.token]);

  useEffect(() => {
    if (!transferModal.open) return;
    const identifier = String(transferModal.identifier || "").trim();
    if (identifier.length < 1) {
      setTransferModal((current) => ({
        ...current,
        suggestions: [],
        suggestionsOpen: false,
        loading: false,
        selectedMerchant: null,
      }));
      return;
    }

    let cancelled = false;
    setTransferModal((current) => ({ ...current, loading: true, error: "" }));
    const timer = window.setTimeout(async () => {
      try {
        const { data: merchantData } = await axios.get(`${API}/merchants`, {
          headers: userInfo?.token
            ? { Authorization: `Bearer ${userInfo.token}` }
            : {},
          params: { q: identifier, page: 1, limit: 8 },
        });
        if (cancelled) return;
        setTransferModal((current) => ({
          ...current,
          suggestions: merchantData?.items || [],
          suggestionsOpen: !current.selectedMerchant,
          loading: false,
        }));
      } catch (err) {
        if (cancelled) return;
        setTransferModal((current) => ({
          ...current,
          suggestions: [],
          suggestionsOpen: false,
          loading: false,
          error:
            err?.response?.data?.message ||
            err.message ||
            "Unable to fetch merchant suggestions.",
        }));
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [transferModal.open, transferModal.identifier, userInfo?.token]);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (connectionInitialized.current) {
        toast.success("Internet connection restored.");
      }
      connectionInitialized.current = true;
    };
    const handleOffline = () => {
      setIsOnline(false);
      connectionInitialized.current = true;
      toast.error("Your internet connection is gone. Please reconnect before importing data.", {
        autoClose: 5000,
      });
    };
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    connectionInitialized.current = true;
    if (!navigator.onLine) handleOffline();
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!message) return;

    const isSuccess = /^(Enquiry |Import done|Import successful|Exported |Deleted |Updated enquiry|Welcome call|Full payment|Partial payment|Payment completed)/i.test(message);
    toast(message, {
      type: isSuccess ? "success" : "error",
      hideProgressBar: true,
      autoClose: 2500,
    });
  }, [message]);

  const getDateRange = () => {
    const today = dayjs();
    if (dateFilter === "daily") {
      const date = today.format("YYYY-MM-DD");
      return { dateFrom: date, dateTo: date };
    }
    if (dateFilter === "weekly") {
      const daysSinceMonday = (today.day() + 6) % 7;
      const weekStart = today.subtract(daysSinceMonday, "day");
      return {
        dateFrom: weekStart.format("YYYY-MM-DD"),
        dateTo: weekStart.add(6, "day").format("YYYY-MM-DD"),
      };
    }
    if (dateFilter === "monthly") {
      return {
        dateFrom: today.subtract(1, "month").format("YYYY-MM-DD"),
        dateTo: today.format("YYYY-MM-DD"),
      };
    }
    if (dateFilter === "custom") {
      return { dateFrom: customDateFrom, dateTo: customDateTo };
    }
    return {};
  };

  const fetchList = async (search = q) => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (effectiveMerchantId) params.merchantId = effectiveMerchantId;
      if (sendBack) params.sendBack = "true";
      if (!sendBack && sentBackFilter !== "all")
        params.sentBackStatus = sentBackFilter;
      if (search) params.q = search;
      if (statusFilter !== "all") params.status = statusFilter;
      Object.assign(params, getDateRange());
      if (selectedDisburshMonths.length) {
        params.disburshMonths = selectedDisburshMonths.join(",");
      }
      const { data } = await axios.get(`${API}/enquiries`, {
        ...authConfig,
        params,
      });
      const items = data.items || [];
      setData({
        items,
        total: data.total ?? items.length,
        sentBackTotal: data.sentBackTotal ?? 0,
        pages: data.pages ?? Math.ceil((data.total || items.length) / limit),
        page: data.page ?? page,
      });
      setSelectedIds([]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userInfo?.token && userInfo?.role?._id) dispatch(getRole());
  }, [dispatch, userInfo?.token, userInfo?.role?._id]);

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedQ(q), 350);
    return () => clearTimeout(timeout);
  }, [q]);

  useEffect(() => {
    if (canViewEnquiries) fetchList(debouncedQ);
  }, [
    page,
    limit,
    effectiveMerchantId,
    sendBack,
    sentBackFilter,
    debouncedQ,
    dateFilter,
    customDateFrom,
    customDateTo,
    selectedDisburshMonths,
    statusFilter,
    canViewEnquiries,
  ]);

  const allVisibleSelected =
    data.items.length > 0 &&
    data.items.every((item) => selectedIds.includes(item._id));
  const displayedItems =
    serialSort === "desc" ? [...data.items].reverse() : data.items;

  const toggleOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const toggleAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedIds((prev) =>
        prev.filter((id) => !data.items.some((item) => item._id === id)),
      );
    } else {
      setSelectedIds((prev) => [
        ...new Set([...prev, ...data.items.map((item) => item._id)]),
      ]);
    }
  };

  const downloadTemplate = () => {
    const headers = [
      "Merchant",
      "Identifier",
      "Lead Date (D/M/YYYY)",
      "Disbursh Month",
      "Case Name",
      "Mobile Number",
      "Ref.by",
      "Loan type",
      "Amount require",
      "Login from",
      "Status",
      "Remarks",
      "Workable/ Notworkable",
      "Fe Login Amount",
      "Fe Disburshment Amount",
      "Bank payments",
      "Login Fees Status",
      "Fe Disburshment Amount",
      "For next month Followup",
    ];
    const ws = XLSX.utils.aoa_to_sheet([headers]);
    ws["!cols"] = headers.map(() => ({ wch: 20 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Enquiries");
    XLSX.writeFile(wb, "loan_enquiry_template.xlsx");
  };

  const exportExcel = async () => {
    if (sendBack || isMerchantUser) return;

    setExporting(true);
    setMessage("");
    try {
      const exportColumns = COLUMNS.filter(
        (column) => !["select", "actions"].includes(column.key),
      );
      const totalRows = Math.max(Number(data.total) || data.items.length || 1, 1);
      const params = { page: 1, limit: totalRows };
      if (effectiveMerchantId) params.merchantId = effectiveMerchantId;
      if (sentBackFilter !== "all") params.sentBackStatus = sentBackFilter;
      if (debouncedQ) params.q = debouncedQ;
      if (statusFilter !== "all") params.status = statusFilter;
      Object.assign(params, getDateRange());
      if (selectedDisburshMonths.length) {
        params.disburshMonths = selectedDisburshMonths.join(",");
      }

      const { data: exportData } = await axios.get(`${API}/enquiries`, {
        ...authConfig,
        params,
      });
      const rows = exportData.items || [];
      const sheetRows = rows.map((row, index) =>
        Object.fromEntries(
          exportColumns.map((column) => [
            column.label,
            exportCell(row, column, index),
          ]),
        ),
      );

      const ws = XLSX.utils.json_to_sheet(sheetRows, {
        header: exportColumns.map((column) => column.label),
      });
      ws["!cols"] = exportColumns.map((column) => ({
        wch: Math.max(14, Math.min(32, column.label.length + 6)),
      }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Loan Enquiries");
      XLSX.writeFile(wb, `loan_enquiries_${dayjs().format("YYYY-MM-DD")}.xlsx`);
      setMessage(`Exported ${rows.length} loan enquiry record${rows.length === 1 ? "" : "s"}.`);
    } catch (err) {
      setMessage(
        err?.response?.data?.message || err.message || "Excel export failed",
      );
    } finally {
      setExporting(false);
    }
  };

  const submitExcelFile = async (file) => {
    if (!file) return;
    if (!navigator.onLine) {
      setMessage("Your internet connection is gone. No data was inserted.");
      toast.error("Your internet connection is gone. No data was inserted.");
      return;
    }

    const body = new FormData();
    body.append("file", file);
    setUploading(true);
    setMessage("");
    setUploadResult(null);
    setUploadProgress(0);
    const uploadController = new AbortController();
    const cancelWhenOffline = () => uploadController.abort();
    window.addEventListener("offline", cancelWhenOffline, { once: true });
    try {
      const { data } = await axios.post(
        `${API}/enquiries/upload-excel`,
        body,
        {
          ...authConfig,
          signal: uploadController.signal,
          onUploadProgress: (event) => {
            if (!event.total) return;
            setUploadProgress(Math.min(100, Math.round((event.loaded * 100) / event.total)));
          },
        },
      );
      setUploadProgress(100);
      setUploadResult(data);
      const drafts = (data.failedRows || []).map((failedRow) => {
        const rawLeadDate = failedRow.errors?.leadDate
          ? failedRow.values?._leadDateOriginal || failedRow.values?.leadDate || ""
          : failedRow.values?.leadDate;
        const leadDate = importDateValue(rawLeadDate);
        const errors = { ...(failedRow.errors || {}) };
        if (!validateImportField("leadDate", leadDate)) delete errors.leadDate;
        return {
          sourceRow: failedRow.row,
          values: { ...(failedRow.values || {}), leadDate },
          errors,
        };
      });
      setFailedDrafts(drafts);
      setFailedPreviewPage(1);
      setFailedPreviewOpen(drafts.length > 0);
      setMessage(
        data.failed
          ? `Import completed with errors: ${data.inserted || 0} succeeded and ${data.failed} failed.`
          : `Import successful: all ${data.inserted || data.totalRows || 0} entries were inserted.`,
      );
      setPage(1);
      await fetchList();
    } catch (err) {
      setFailedPreviewOpen(false);
      const offline = !navigator.onLine || err?.code === "ERR_NETWORK";
      setMessage(offline
        ? "Your internet connection is gone. The import could not be completed."
        : `Import failed: ${err?.response?.data?.message || err.message || "Excel upload failed"}`,
      );
    } finally {
      window.removeEventListener("offline", cancelWhenOffline);
      setUploading(false);
    }
  };

  const uploadExcel = async (file) => {
    if (!file) return;
    setImportParsing(true);
    setMessage("");
    setUploadResult(null);
    try {
      const workbook = XLSX.read(await file.arrayBuffer(), {
        type: "array",
        cellDates: true,
      });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const rawRows = XLSX.utils.sheet_to_json(worksheet, { defval: "", raw: false });
      const rows = rawRows.map((rawRow, index) => {
        const values = Object.fromEntries(
          IMPORT_FIELDS.map(([field, header]) => {
            const rawValue = rawRow[header] ?? "";
            if (field !== "caseName" && isNotAvailableValue(rawValue)) return [field, ""];
            if (field === "mobileNumber") return [field, sanitizePhoneNumbers(rawValue)];
            return [field, field === "leadDate" ? importDateValue(rawValue) : rawValue];
          }),
        );
        const errors = Object.fromEntries(
          IMPORT_FIELDS.map(([field]) => [field, validateImportField(field, values[field])])
            .filter(([, error]) => error),
        );
        return { sourceRow: index + 2, values, errors };
      });
      if (!rows.length) throw new Error("The selected file does not contain any data rows.");
      setImportPreviewFile(file);
      setImportPreviewRows(rows);
      setImportPreviewPage(1);
      setImportOpen(false);
      setImportPreviewOpen(true);
    } catch (err) {
      setMessage(`Import preview failed: ${err.message || "Unable to read this file."}`);
    } finally {
      setImportParsing(false);
    }
  };

  const confirmPreviewImport = async () => {
    if (!importPreviewFile || uploading) return;
    if (!navigator.onLine) {
      toast.error("Your internet connection is gone. No data was inserted.");
      return;
    }

    const hasValidationErrors = importPreviewRows.some(
      (row) => Object.keys(row.errors).length > 0,
    );
    if (hasValidationErrors) {
      setFailedDrafts(
        importPreviewRows.map((row) => ({
          sourceRow: row.sourceRow,
          values: { ...row.values },
          errors: { ...row.errors },
        })),
      );
      setFailedPreviewPage(1);
      setImportPreviewOpen(false);
      setFailedPreviewOpen(true);
      toast.error("Please correct the highlighted entries before importing.");
      return;
    }

    setImportPreviewOpen(false);
    setImportOpen(true);
    await submitExcelFile(importPreviewFile);
  };

  const updateFailedDraft = (draftIndex, field, value) => {
    const fieldError = validateImportField(field, value);
    setFailedDrafts((current) =>
      current.map((draft, index) =>
        index === draftIndex
          ? {
              ...draft,
              values: { ...draft.values, [field]: value },
              errors: {
                ...Object.fromEntries(
                  Object.entries(draft.errors).filter(
                    ([errorField]) => errorField !== field,
                  ),
                ),
                ...(fieldError ? { [field]: fieldError } : {}),
              },
            }
          : draft,
      ),
    );
  };

  const retryFailedRows = async () => {
    if (
      !failedDrafts.length ||
      uploading ||
      failedDrafts.some((draft) => Object.keys(draft.errors).length > 0)
    ) return;
    const rows = failedDrafts.map(({ values }) =>
      Object.fromEntries(
        IMPORT_FIELDS.map(([field, header]) => [header, values[field] ?? ""]),
      ),
    );
    const worksheet = XLSX.utils.json_to_sheet(rows, {
      header: IMPORT_FIELDS.map(([, header]) => header),
    });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Enquiries");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const file = new File([excelBuffer], "corrected_loan_enquiries.xlsx", {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    setFailedPreviewOpen(false);
    setSelectedImportFile(null);
    setImportOpen(true);
    await submitExcelFile(file);
  };

  const jumpToNextImportError = () => {
    const errors = [];
    failedDrafts.forEach((draft, draftIndex) => {
      IMPORT_FIELDS.forEach(([field], fieldIndex) => {
        if (draft.errors[field]) {
          errors.push({
            key: `${draftIndex}-${field}`,
            draftIndex,
            fieldIndex,
            position: draftIndex * IMPORT_FIELDS.length + fieldIndex,
          });
        }
      });
    });
    if (!errors.length) return;

    const activeKey = document.activeElement?.dataset?.errorKey;
    const activeError = errors.find((error) => error.key === activeKey);
    const currentPosition = activeError?.position ?? lastJumpedError.current?.position ?? -1;
    const target =
      errors.find((error) => error.position > currentPosition) || errors[0];

    lastJumpedError.current = target;
    setFailedPreviewPage(
      Math.floor(target.draftIndex / FAILED_ROWS_PER_PAGE) + 1,
    );
    window.setTimeout(() => {
      const element = failedFieldRefs.current[target.key];
      element?.scrollIntoView({ behavior: "smooth", block: "center" });
      element?.focus({ preventScroll: true });
    }, 100);
  };

  const deleteSelected = () => {
    if (!canDeleteEnquiry || !selectedIds.length) return;
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteSelected = async () => {
    if (!canDeleteEnquiry || !selectedIds.length) return;
    setDeleting(true);
    setMessage("");
    try {
      const { data } = await axios.delete(`${API}/enquiries/bulk`, {
        ...authConfig,
        data: { ids: selectedIds },
      });
      toast.success(`Deleted ${data.deletedCount || 0} records successfully.`, {
        hideProgressBar: true,
        autoClose: 2500,
      });
      setDeleteConfirmOpen(false);
      await fetchList();
    } catch (err) {
      setMessage(
        err?.response?.data?.message || err.message || "Delete failed",
      );
    } finally {
      setDeleting(false);
    }
  };

  const sendBackEnquiry = async (row) => {
    if (!row?._id) return;
    setSendBackModal({ open: true, row, mode: "merchant" });
  };

  const confirmSendBackEnquiry = async () => {
    const row = sendBackModal.row;
    if (!row?._id) return;
    setMessage("");
    try {
      await axios.put(
        `${API}/enquiries/${row._id}/send-back`,
        {
          merchantName: row.merchantName || row.merchant?.name || "",
          merchantIdentifier: row.merchantIdentifier || row.merchant?.identifier || "",
        },
        authConfig,
      );
      setMessage("Enquiry moved to send back enquiries.");
      setSendBackModal({ open: false, row: null, mode: "" });
      await fetchList();
    } catch (err) {
      setMessage(
        err?.response?.data?.message || err.message || "Send back failed",
      );
    }
  };

  const restoreEnquiry = async (row) => {
    if (!row?._id) return;
    const ok = window.confirm(
      `Restore enquiry for ${row.caseName || "this case"}?`,
    );
    if (!ok) return;

    setMessage("");
    try {
      await axios.put(`${API}/enquiries/${row._id}/restore`, {}, authConfig);
      setMessage("Enquiry restored to loan enquiries.");
      await fetchList();
    } catch (err) {
      setMessage(
        err?.response?.data?.message || err.message || "Restore failed",
      );
    }
  };

  const sendBackToAdmin = async (row) => {
    if (!row?._id) return;
    setSendBackModal({ open: true, row, mode: "admin" });
  };

  const confirmSendBackToAdmin = async () => {
    const row = sendBackModal.row;
    if (!row?._id) return;
    setMessage("");
    try {
      await axios.put(
        `${API}/enquiries/${row._id}/resubmit-send-back`,
        {},
        authConfig,
      );
      setMessage("Updated enquiry sent back to admin.");
      setSendBackModal({ open: false, row: null, mode: "" });
      await fetchList();
    } catch (err) {
      setMessage(
        err?.response?.data?.message ||
          err.message ||
          "Send back to admin failed",
      );
    }
  };

  const updateWelcomeCallMessage = async (row, value) => {
    if (!row?._id || !canEditEnquiry) return;
    setMessage("");
    try {
      await axios.put(
        `${API}/enquiries/${row._id}/welcome-call-message`,
        { welcomeCallMessage: value },
        authConfig,
      );
      setMessage(`Welcome call/message marked ${value ? "Yes" : "No"}.`);
      await fetchList();
    } catch (err) {
      setMessage(
        err?.response?.data?.message ||
          err.message ||
          "Welcome call/message update failed",
      );
    }
  };

  const confirmWelcomeCallMessage = async () => {
    const row = welcomeConfirmModal.row;
    if (!row?._id) return;
    setWelcomeConfirmModal({ open: false, row: null });
    await updateWelcomeCallMessage(row, true);
  };

  const approveEnquiry = async (row) => {
    if (!row?._id || !canApproveEnquiry) return;
    if (!row.welcomeCallMessage) {
      setMessage("Mark Welcome Call/Message as Yes before approving.");
      return;
    }
    setApproveModal({ open: true, row });
    return;
    /*

    const ok = window.confirm(`Approve enquiry for ${row.caseName || "this case"} and add it to merchant account?`);
    if (!ok) return;

    setMessage("");
    try {
      await axios.put(`${API}/enquiries/${row._id}/approve`, {}, authConfig);
      setMessage("Enquiry approved. Added to merchant account.");
      await fetchList();
    } catch (err) {
      setMessage(
        err?.response?.data?.message ||
          err.message ||
          "Approve failed",
      );
    }
    */
  };

  const confirmApproveEnquiry = async () => {
    const row = approveModal.row;
    if (!row?._id || !canApproveEnquiry) return;

    setMessage("");
    try {
      await axios.put(`${API}/enquiries/${row._id}/approve`, {}, authConfig);
      setMessage("Enquiry approved. Added to merchant account.");
      setApproveModal({ open: false, row: null });
      await fetchList();
    } catch (err) {
      setMessage(
        err?.response?.data?.message ||
          err.message ||
          "Approve failed",
      );
    }
  };

  const initiatePayment = async (row) => {
    if (!row?._id || !canApproveEnquiry || !row.approved || row.paid) return;
    const { outstanding } = getPaymentAmounts(row);
    setPaymentModal({
      open: true,
      row,
      file: null,
      amounts: {
        lead: String(outstanding.lead),
        login: String(outstanding.login),
        disbursement: String(outstanding.disbursement),
      },
      error: "",
    });
  };

  const confirmInitiatePayment = async () => {
    const row = paymentModal.row;
    if (!row?._id || !canApproveEnquiry || !row.approved || row.paid) return;
    if (!paymentModal.file) {
      setMessage("Select a payment proof image before initiating payment.");
      setPaymentModal((current) => ({
        ...current,
        error: "Select a payment proof image before initiating payment.",
      }));
      return;
    }

    setMessage("");
    setPaymentModal((current) => ({ ...current, error: "" }));
    setPaymentSubmitting(true);
    try {
      const payload = new FormData();
      payload.append("paymentProofImage", paymentModal.file);
      payload.append("paymentLeadAmount", paymentModal.amounts.lead || "0");
      payload.append("paymentLoginAmount", paymentModal.amounts.login || "0");
      payload.append(
        "paymentDisbursementAmount",
        paymentModal.amounts.disbursement || "0",
      );
      const { data: paymentResult } = await axios.put(
        `${API}/enquiries/${row._id}/initiate-payment`,
        payload,
        authConfig,
      );
      const updatedRow = paymentResult?.enquiry;
      const paymentComplete = updatedRow?.paid === true;
      const hasOutstandingPayment =
        getPaymentAmounts(updatedRow).outstandingTotal > 0;
      setMessage(
        paymentComplete
          ? "Full payment completed and deducted from the merchant wallet."
          : hasOutstandingPayment
            ? "Partial payment completed. Initiate the remaining payment when ready."
            : "Payment completed. Further payment will be available when another fee amount is added.",
      );
      setPaymentModal({
        open: false,
        row: null,
        file: null,
        amounts: { lead: "", login: "", disbursement: "" },
        error: "",
      });
      await fetchList();
    } catch (err) {
      const paymentError =
        err?.response?.data?.message ||
        err.message ||
        "Payment initiation failed";
      setMessage(paymentError);
      setPaymentModal((current) => ({ ...current, error: paymentError }));
    } finally {
      setPaymentSubmitting(false);
    }
  };

  const openEdit = (row) => {
    if (!canEditRow(row)) {
      setMessage("You do not have permission to edit loan enquiries.");
      return;
    }
    setEditingRow(row);
    setEditForm(getEditFormValues(row));
    setRejectionReasonError(false);
    setEditFiles(emptyEditFiles);
  };

  const closeEdit = () => {
    setEditingRow(null);
    setEditForm(emptyEditForm);
    setRejectionReasonError(false);
    setEditFiles(emptyEditFiles);
    setTransferModal({
      open: false,
      row: null,
      identifier: "",
      selectedMerchant: null,
      suggestions: [],
      suggestionsOpen: false,
      loading: false,
      saving: false,
      error: "",
    });
  };

  const changeEdit = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const changeEditFile = (field, file) => {
    setEditFiles((prev) => ({ ...prev, [field]: file }));
  };

  const openTransferMerchant = () => {
    if (!editingRow?._id) return;
    setTransferModal({
      open: true,
      row: editingRow,
      identifier: "",
      selectedMerchant: null,
      suggestions: [],
      suggestionsOpen: false,
      loading: false,
      saving: false,
      error: "",
    });
  };

  const closeTransferMerchant = () => {
    setTransferModal({
      open: false,
      row: null,
      identifier: "",
      selectedMerchant: null,
      suggestions: [],
      suggestionsOpen: false,
      loading: false,
      saving: false,
      error: "",
    });
  };

  const selectTransferMerchant = (merchant) => {
    setTransferModal((current) => ({
      ...current,
      identifier: merchant?.identifier || "",
      selectedMerchant: merchant,
      suggestionsOpen: false,
      error: "",
    }));
  };

  const confirmTransferMerchant = async () => {
    const row = transferModal.row;
    if (!row?._id) return;
    const identifier = String(transferModal.identifier || "").trim();
    if (!identifier) {
      setTransferModal((current) => ({
        ...current,
        error: "Enter a merchant identifier before transferring.",
      }));
      return;
    }

    const selectedMerchant =
      transferModal.selectedMerchant ||
      transferModal.suggestions.find(
        (merchant) =>
          String(merchant.identifier || "").trim().toLowerCase() ===
          identifier.toLowerCase(),
      );

    if (!selectedMerchant) {
      setTransferModal((current) => ({
        ...current,
        error: "Select a merchant from the suggestions before transferring.",
      }));
      return;
    }

    setTransferModal((current) => ({ ...current, saving: true, error: "" }));
    setMessage("");
    try {
      const { data: transferResult } = await axios.put(
        `${API}/enquiries/${row._id}/transfer`,
        {
          merchantIdentifier: selectedMerchant.identifier,
          merchantName: selectedMerchant.name,
        },
        {
          headers: { ...authConfig.headers, "Content-Type": "application/json" },
        },
      );
      setEditForm((current) => ({
        ...current,
        merchantIdentifier: selectedMerchant.identifier,
        merchantName: selectedMerchant.name,
      }));
      setEditingRow((current) =>
        current
          ? {
              ...current,
              ...(transferResult?.enquiry || {}),
              merchantIdentifier: selectedMerchant.identifier,
              merchantName: selectedMerchant.name,
            }
          : current,
      );
      setMessage("Merchant transferred successfully.");
      closeTransferMerchant();
      await fetchList();
    } catch (err) {
      const validationErrors = err?.response?.data?.errors;
      setTransferModal((current) => ({
        ...current,
        saving: false,
        error:
          err?.response?.data?.message ||
          (validationErrors ? Object.values(validationErrors).join(" ") : "") ||
          err.message ||
          "Merchant transfer failed.",
      }));
    }
  };

  const saveEdit = async () => {
    if (!editingRow?._id) return;
    if (!canEditRow(editingRow)) {
      setMessage("You do not have permission to edit loan enquiries.");
      closeEdit();
      return;
    }
    if (!/^\d{10}(?:\/\d{10})?$/.test(String(editForm.mobileNumber || "").trim())) {
      setMessage("Enter 10 digits, or two 10-digit numbers separated by /.");
      return;
    }
    if (
      editForm.status === "Rejected" &&
      !String(editForm.sendBackReason || "").trim()
    ) {
      setRejectionReasonError(true);
      setMessage("Rejection reason is required.");
      return;
    }
    setRejectionReasonError(false);
    const hasFiles = Object.values(editFiles).some(Boolean);
    const merchantEditableFields = new Set([
      "caseName",
      "mobileNumber",
      "referredBy",
      "loanType",
      "amountRequired",
      "remarks",
      "additionalDocumentType",
    ]);
    const originalForm = getEditFormValues(editingRow);
    const formPayload = Object.fromEntries(
      Object.entries(editForm).filter(
        ([key, value]) =>
          (!isMerchantUser || merchantEditableFields.has(key)) &&
          String(value ?? "") !== String(originalForm[key] ?? ""),
      ),
    );
    let payload = formPayload;
    const headers = authConfig.headers;

    if (hasFiles) {
      const body = new FormData();
      Object.entries(formPayload).forEach(([key, value]) =>
        body.append(key, value),
      );
      Object.entries(editFiles).forEach(([key, file]) => {
        if (file) body.append(key, file);
      });
      payload = body;
    }

    setSavingEdit(true);
    setMessage("");
    try {
      await axios.put(`${API}/enquiries/${editingRow._id}`, payload, {
        headers: hasFiles
          ? headers
          : { ...headers, "Content-Type": "application/json" },
      });
      setMessage("Enquiry updated.");
      closeEdit();
      await fetchList();
    } catch (err) {
      const validationErrors = err?.response?.data?.errors;
      setMessage(
        err?.response?.data?.message ||
          (validationErrors
            ? Object.values(validationErrors).join(" ")
            : "") ||
          err.message ||
          "Update failed",
      );
    } finally {
      setSavingEdit(false);
    }
  };

  const editInput = (field, label, type = "text", disabled = false) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <input
        type={type}
        value={editForm[field]}
        disabled={disabled}
        inputMode={field === "mobileNumber" ? "tel" : undefined}
        maxLength={field === "mobileNumber" ? 21 : undefined}
        onChange={(e) =>
          changeEdit(
            field,
            field === "mobileNumber"
              ? sanitizePhoneNumbers(e.target.value)
              : e.target.value,
          )
        }
        className={`${inputCls} disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500`}
      />
    </div>
  );

  const editDocumentInput = (field, label) => (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
      <div className="flex items-center justify-between gap-3 mb-2">
        <p className="text-sm font-medium text-gray-700">{label}</p>
        {editingRow?.[field] && (
          <a
            href={getDocumentUrl(editingRow[field])}
            target="_blank"
            rel="noreferrer"
            className="text-xs font-medium text-blue-600 hover:text-blue-800"
          >
            Current
          </a>
        )}
      </div>
      <label className="inline-flex items-center justify-center border border-gray-300 hover:bg-white text-gray-700 text-sm font-medium px-3 py-2 rounded-lg transition-colors cursor-pointer">
        Replace Image
        <input
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => changeEditFile(field, e.target.files?.[0] || null)}
        />
      </label>
      <p className="mt-2 text-xs text-gray-500 truncate">
        {editFiles[field]?.name || "No replacement selected"}
      </p>
    </div>
  );

  const paymentRow = paymentModal.row;
  const paymentDetails = getPaymentAmounts(paymentRow);
  const paymentAmounts = paymentDetails.outstanding;
  const paymentOutstandingTotal = paymentDetails.outstandingTotal;
  const paymentTotal =
    (Number(paymentModal.amounts?.lead) || 0) +
    (Number(paymentModal.amounts?.login) || 0) +
    (Number(paymentModal.amounts?.disbursement) || 0);
  const paymentBalanceAfter = Math.max(paymentOutstandingTotal - paymentTotal, 0);
  const paymentAmountsValid = ["lead", "login", "disbursement"].every(
    (key) => {
      const value = Number(paymentModal.amounts?.[key] || 0);
      return Number.isFinite(value) && value >= 0 && value <= paymentAmounts[key];
    },
  );
  const formatPaymentAmount = (value) =>
    `₹${Number(value || 0).toLocaleString("en-IN")}`;
  const selectedLedger = getPaymentLedger(ledgerModal.row);
  const selectedLedgerOutstanding = getPaymentAmounts(
    ledgerModal.row,
  ).outstandingTotal;
  const activeDateRange = getDateRange();
  const formatRangeDate = (date) =>
    date && dayjs(date).isValid() ? dayjs(date).format("DD MMM YYYY") : "—";
  const activeDateLabel =
    activeDateRange.dateFrom || activeDateRange.dateTo
      ? `${formatRangeDate(activeDateRange.dateFrom)} to ${formatRangeDate(activeDateRange.dateTo)}`
      : "All lead dates";

  return (
    <div className="-mx-3 px-2 py-3 sm:mx-0 sm:p-6 bg-gray-50 min-h-screen w-[calc(100%+1.5rem)] sm:w-full">
      {!canViewEnquiries && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 max-w-lg">
          <h1 className="text-xl font-semibold text-gray-800 mb-2">
            Not authorized
          </h1>
          <p className="text-sm text-gray-500">
            You do not have permission to view this enquiry list.
          </p>
        </div>
      )}
      {canViewEnquiries && (
        <>
          <Modal
            open={deleteConfirmOpen}
            title="Delete enquiries?"
            onClose={() => !deleting && setDeleteConfirmOpen(false)}
            maxWidth="max-w-md"
            footer={
              <>
                <button
                  type="button"
                  onClick={() => setDeleteConfirmOpen(false)}
                  disabled={deleting}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDeleteSelected}
                  disabled={deleting}
                  className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-300"
                >
                  <Trash2 size={16} />
                  {deleting ? "Deleting..." : "Yes, delete"}
                </button>
              </>
            }
          >
            <div className="flex gap-4 rounded-xl border border-red-100 bg-red-50 p-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
                <AlertTriangle size={21} />
              </div>
              <div>
                <p className="font-semibold text-gray-900">
                  You are about to delete {selectedIds.length}{" "}
                  {selectedIds.length === 1 ? "enquiry" : "enquiries"}.
                </p>
                <p className="mt-1 text-sm leading-5 text-gray-600">
                  This action cannot be undone. The selected records will be
                  permanently removed.
                </p>
              </div>
            </div>
          </Modal>

          <Modal
            open={importOpen}
            title="Import Excel"
            maxWidth="max-w-2xl"
            onClose={() => !importParsing && !uploading && setImportOpen(false)}
            footer={
              <>
                <button
                  type="button"
                  onClick={() => setImportOpen(false)}
                  disabled={importParsing || uploading}
                  className="border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                >
                  Close
                </button>
                {selectedImportFile && (
                  <button
                    type="button"
                    onClick={() => uploadExcel(selectedImportFile)}
                    disabled={importParsing || uploading || !isOnline}
                    className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                  >
                    {importParsing ? "Preparing Preview…" : "Confirm"}
                  </button>
                )}
              </>
            }
          >
            <div className="space-y-3">
              <div className={`rounded-lg border px-3 py-2 text-sm font-medium ${isOnline ? "border-green-200 bg-green-50 text-green-700" : "border-red-200 bg-red-50 text-red-700"}`}>
                {isOnline ? "Internet connected — ready to import." : "Internet connection lost — importing is disabled."}
              </div>
              {/* Template download */}
              <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-blue-800 mb-0.5">
                    Download Template
                  </p>
                  <p className="text-xs text-blue-600">
                    Use this template to fill your data. Required fields are
                    marked with *. Enter Lead Date as D/M/YYYY (day/month/year).
                  </p>
                </div>
                <button
                  type="button"
                  onClick={downloadTemplate}
                  className="shrink-0 border border-blue-300 bg-white hover:bg-blue-50 text-blue-700 text-sm font-medium px-4 py-2 rounded-lg transition-colors"
                >
                  Download .xlsx
                </button>
              </div>

              {/* Upload area */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  Upload File
                </p>
                <label
                  className={`flex flex-col items-center justify-center gap-1.5 border-2 border-dashed rounded-lg p-4 cursor-pointer transition-colors ${uploading ? "border-gray-200 bg-gray-50 cursor-not-allowed" : selectedImportFile ? "border-blue-300 bg-blue-50" : "border-gray-300 hover:border-blue-400 hover:bg-blue-50"}`}
                >
                  <svg
                    className="w-7 h-7 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
                    />
                  </svg>
                  <span className="text-sm font-medium text-gray-600">
                    {uploading
                      ? "Uploading…"
                      : selectedImportFile
                        ? selectedImportFile.name
                      : importParsing
                        ? "Reading file and preparing preview…"
                        : "Click to choose .xlsx / .xls / .csv"}
                  </span>
                  <span className="text-xs text-gray-400">
                    {selectedImportFile ? "Click to choose a different file" : "First row must be the header row"}
                  </span>
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    className="hidden"
                    disabled={uploading || importParsing}
                    onChange={(e) => {
                      setUploadResult(null);
                      setMessage("");
                      setSelectedImportFile(e.target.files?.[0] || null);
                      e.target.value = "";
                    }}
                  />
                </label>
              </div>

              {(uploading || uploadResult) && (
                <div className={`rounded-lg border p-3 ${uploadResult ? "border-green-200 bg-green-50" : "border-blue-200 bg-blue-50"}`}>
                  <div className={`mb-2 flex items-center justify-between text-xs font-semibold ${uploadResult ? "text-green-700" : "text-blue-700"}`}>
                    <span>{uploadResult ? `${uploadResult.inserted || 0} entries imported` : "Importing entries…"}</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className={`h-2.5 overflow-hidden rounded-full ${uploadResult ? "bg-green-100" : "bg-blue-100"}`}>
                    <div
                      className={`h-full rounded-full transition-all duration-300 ${uploadResult ? "bg-green-600" : "bg-blue-600"}`}
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className={`mt-2 text-xs ${uploadResult ? "text-green-600" : "text-blue-600"}`}>
                    {uploadResult ? "Import completed." : "Keep this window open until the import finishes."}
                  </p>
                </div>
              )}

              {/* Upload result */}
              {uploadResult && (
                <div className="space-y-3">
                  {/* Summary bar */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-center">
                      <p className="text-xs text-gray-500 mb-0.5">Total Rows</p>
                      <p className="text-lg font-bold text-gray-800">
                        {uploadResult.totalRows}
                      </p>
                    </div>
                    <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-center">
                      <p className="text-xs text-green-600 mb-0.5">Inserted</p>
                      <p className="text-lg font-bold text-green-700">
                        {uploadResult.inserted}
                      </p>
                    </div>
                    <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-center">
                      <p className="text-xs text-red-500 mb-0.5">Failed</p>
                      <p className="text-lg font-bold text-red-600">
                        {uploadResult.failed}
                      </p>
                    </div>
                  </div>

                  {/* Failed rows detail */}
                  {uploadResult.failedRows?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-red-500 uppercase tracking-wider mb-2">
                        Failed Rows{" "}
                        {uploadResult.failed > uploadResult.failedRows.length
                          ? `(showing first ${uploadResult.failedRows.length} of ${uploadResult.failed})`
                          : ""}
                      </p>
                      <button
                        type="button"
                        onClick={() => setFailedPreviewOpen(true)}
                        className="mb-2 rounded-lg bg-red-600 px-3 py-2 text-xs font-semibold text-white hover:bg-red-700"
                      >
                        Preview and fix failed rows
                      </button>
                      <div className="max-h-52 overflow-y-auto rounded-lg border border-red-100">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-red-50 border-b border-red-100">
                              <th className="px-3 py-2 text-left font-semibold text-red-600 w-16">
                                Row
                              </th>
                              <th className="px-3 py-2 text-left font-semibold text-red-600">
                                Errors
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-red-50">
                            {uploadResult.failedRows.map(({ row, errors }) => (
                              <tr key={row} className="hover:bg-red-50">
                                <td className="px-3 py-2 font-mono text-gray-500">
                                  {row}
                                </td>
                                <td className="px-3 py-2 text-gray-700">
                                  {Object.entries(errors).map(
                                    ([field, msg]) => (
                                      <span
                                        key={field}
                                        className="inline-block mr-2 mb-1 bg-red-100 text-red-700 rounded px-1.5 py-0.5"
                                      >
                                        {msg}
                                      </span>
                                    ),
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {message && !uploadResult && (
                <p
                  className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700"
                >
                  Import error: {message}
                </p>
              )}
            </div>
          </Modal>

          <Modal
            open={importPreviewOpen}
            title="Review Entries Before Import"
            maxWidth="max-w-6xl"
            onClose={() => !uploading && setImportPreviewOpen(false)}
            footer={
              <>
                <button
                  type="button"
                  onClick={() => setImportPreviewOpen(false)}
                  disabled={uploading}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmPreviewImport}
                  disabled={uploading || !isOnline || !importPreviewRows.length}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                >
                  {isOnline ? `Import ${importPreviewRows.length} Entries` : "Waiting for Internet"}
                </button>
              </>
            }
          >
            <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <p className="text-xs text-gray-500">File</p>
                <p className="truncate text-sm font-semibold text-gray-800">{importPreviewFile?.name}</p>
              </div>
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                <p className="text-xs text-blue-600">Total Entries</p>
                <p className="text-lg font-bold text-blue-800">{importPreviewRows.length}</p>
              </div>
              <div className={`rounded-lg border p-3 ${importPreviewRows.some((row) => Object.keys(row.errors).length) ? "border-red-200 bg-red-50" : "border-gray-200 bg-gray-50"}`}>
                <p className="text-xs text-red-600">Failed Import</p>
                <p className="text-lg font-bold text-red-800">
                  {importPreviewRows.filter((row) => Object.keys(row.errors).length).length}
                </p>
              </div>
              <div className="rounded-lg border border-green-200 bg-green-50 p-3">
                <p className="text-xs text-green-600">Ready to import</p>
                <p className="text-lg font-bold text-green-800">
                  {importPreviewRows.filter((row) => Object.keys(row.errors).length === 0).length}
                </p>
              </div>
            </div>
            {(() => {
              const completedEntries = importPreviewRows.filter(
                (row) => Object.keys(row.errors).length === 0,
              ).length;
              const completionPercent = importPreviewRows.length
                ? Math.round((completedEntries / importPreviewRows.length) * 100)
                : 0;

              return (
                <div className="mb-4" aria-label="Import entries progress">
                  <div className="mb-1.5 flex items-center justify-between text-xs font-medium">
                    <span className="text-gray-600">
                      {completedEntries} of {importPreviewRows.length} entries ready
                    </span>
                    <span className="text-blue-700">{completionPercent}%</span>
                  </div>
                  <div
                    className="h-2.5 w-full overflow-hidden rounded-full bg-gray-200"
                    role="progressbar"
                    aria-valuemin="0"
                    aria-valuemax={importPreviewRows.length}
                    aria-valuenow={completedEntries}
                  >
                    <div
                      className="h-full rounded-full bg-green-500 transition-[width] duration-300"
                      style={{ width: `${completionPercent}%` }}
                    />
                  </div>
                </div>
              );
            })()}
            <p className="mb-3 text-sm text-gray-600">
              Review all entries below. Nothing is inserted until you click the Import button.
            </p>
            <div className="max-h-[52vh] overflow-auto rounded-lg border border-gray-200">
              <table className="min-w-max border-collapse text-left text-xs">
                <thead className="sticky top-0 z-20 bg-gray-100">
                  <tr>
                    <th className="sticky left-0 z-30 border-b border-r border-gray-200 bg-gray-100 px-3 py-2">Row</th>
                    <th className="border-b border-r border-gray-200 px-3 py-2">Check</th>
                    {IMPORT_FIELDS.map(([field, label]) => (
                      <th key={field} className="min-w-36 border-b border-r border-gray-200 px-3 py-2 font-semibold text-gray-700">{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {importPreviewRows
                    .slice((importPreviewPage - 1) * FAILED_ROWS_PER_PAGE, importPreviewPage * FAILED_ROWS_PER_PAGE)
                    .map((row) => {
                      const errors = Object.values(row.errors);
                      return (
                        <tr key={row.sourceRow} className="odd:bg-white even:bg-gray-50">
                          <td className="sticky left-0 border-b border-r border-gray-200 bg-inherit px-3 py-2 font-semibold">{row.sourceRow}</td>
                          <td className={`border-b border-r border-gray-200 px-3 py-2 font-semibold ${errors.length ? "text-orange-600" : "text-green-600"}`}>
                            {errors.length ? `${errors.length} issue${errors.length > 1 ? "s" : ""}` : "Ready"}
                          </td>
                          {IMPORT_FIELDS.map(([field]) => (
                            <td key={field} title={row.errors[field] || ""} className={`max-w-52 truncate border-b border-r px-3 py-2 ${row.errors[field] ? "border-red-200 bg-red-50 text-red-700" : "border-gray-200 text-gray-700"}`}>
                              {String(row.values[field] ?? "") || "—"}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
            {importPreviewRows.length > FAILED_ROWS_PER_PAGE && (
              <div className="mt-4 flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Showing {(importPreviewPage - 1) * FAILED_ROWS_PER_PAGE + 1}–{Math.min(importPreviewPage * FAILED_ROWS_PER_PAGE, importPreviewRows.length)} of {importPreviewRows.length}
                </p>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => setImportPreviewPage((page) => Math.max(1, page - 1))} disabled={importPreviewPage === 1} className="rounded-lg border px-3 py-1.5 text-sm disabled:opacity-40">Previous</button>
                  <span className="text-sm font-medium">Page {importPreviewPage} of {Math.ceil(importPreviewRows.length / FAILED_ROWS_PER_PAGE)}</span>
                  <button type="button" onClick={() => setImportPreviewPage((page) => Math.min(Math.ceil(importPreviewRows.length / FAILED_ROWS_PER_PAGE), page + 1))} disabled={importPreviewPage >= Math.ceil(importPreviewRows.length / FAILED_ROWS_PER_PAGE)} className="rounded-lg border px-3 py-1.5 text-sm disabled:opacity-40">Next</button>
                </div>
              </div>
            )}
          </Modal>

          <Modal
            open={failedPreviewOpen}
            title="Fix Failed Import Rows"
            maxWidth="max-w-6xl"
            onClose={() => setFailedPreviewOpen(false)}
            footer={
              <>
                <button
                  type="button"
                  onClick={() => setFailedPreviewOpen(false)}
                  disabled={uploading}
                  className="border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium px-4 py-2 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={retryFailedRows}
                  disabled={
                    uploading ||
                    !failedDrafts.length ||
                    failedDrafts.some(
                      (draft) => Object.keys(draft.errors).length > 0,
                    )
                  }
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white text-sm font-medium px-4 py-2 rounded-lg"
                >
                  {uploading ? "Importing…" : "Import corrected rows"}
                </button>
              </>
            }
          >
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-gray-600">
                Correct the highlighted fields, then import these failed rows again.
              </p>
              <button
                type="button"
                onClick={jumpToNextImportError}
                disabled={!failedDrafts.some((draft) => Object.keys(draft.errors).length)}
                className="rounded-lg border border-red-300 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Jump to next error
              </button>
            </div>
            <div className="max-h-[52vh] overflow-auto rounded-lg border border-gray-200">
              <table className="min-w-max border-collapse text-left">
                <thead className="sticky top-0 z-20 bg-gray-100">
                  <tr>
                    <th className="sticky left-0 z-30 min-w-20 border-b border-r border-gray-200 bg-gray-100 px-3 py-2 text-xs font-semibold text-gray-700">
                      Row
                    </th>
                    <th className="min-w-20 border-b border-r border-gray-200 px-3 py-2 text-xs font-semibold text-gray-700">
                      Errors
                    </th>
                    {IMPORT_FIELDS.map(([field, label]) => (
                      <th
                        key={field}
                        className="min-w-44 border-b border-r border-gray-200 px-3 py-2 text-xs font-semibold text-gray-700"
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {failedDrafts
                    .slice(
                      (failedPreviewPage - 1) * FAILED_ROWS_PER_PAGE,
                      failedPreviewPage * FAILED_ROWS_PER_PAGE,
                    )
                    .map((draft, visibleIndex) => {
                      const draftIndex =
                        (failedPreviewPage - 1) * FAILED_ROWS_PER_PAGE + visibleIndex;
                      const errorCount = Object.keys(draft.errors).length;
                      return (
                        <tr
                          key={`${draft.sourceRow}-${draftIndex}`}
                          className="align-top odd:bg-white even:bg-gray-50"
                        >
                          <td className="sticky left-0 z-10 border-b border-r border-gray-200 bg-inherit px-3 py-3 text-sm font-semibold text-gray-800">
                            {draft.sourceRow}
                          </td>
                          <td className={`border-b border-r border-gray-200 px-3 py-3 text-xs font-medium ${errorCount ? "text-red-600" : "text-green-600"}`}>
                            {errorCount}
                          </td>
                          {IMPORT_FIELDS.map(([field]) => {
                            const error = draft.errors[field];
                            const errorKey = `${draftIndex}-${field}`;
                            const fieldValue = String(draft.values[field] ?? "");
                            const hasInvalidStatusValue =
                              field === "status" &&
                              fieldValue !== "" &&
                              !IMPORT_STATUS_OPTIONS.includes(fieldValue);
                            const suggestedStatus = hasInvalidStatusValue
                              ? suggestedImportStatus(fieldValue)
                              : "";
                            const statusOptions = hasInvalidStatusValue
                              ? (suggestedStatus ? [suggestedStatus] : IMPORT_STATUS_OPTIONS)
                              : IMPORT_STATUS_OPTIONS;
                            const fieldClass = `w-44 rounded-md border px-2.5 py-1.5 text-sm outline-none focus:ring-2 ${error ? "border-red-500 bg-red-50 text-red-900 focus:ring-red-200" : "border-gray-300 bg-white focus:border-blue-500 focus:ring-blue-100"}`;
                            return (
                              <td
                                key={field}
                                className="min-w-44 border-b border-r border-gray-200 px-2 py-2"
                              >
                                {field === "status" ? (
                                  <select
                                    ref={(element) => {
                                      failedFieldRefs.current[errorKey] = element;
                                    }}
                                    data-error-key={errorKey}
                                    value={fieldValue}
                                    onChange={(event) =>
                                      updateFailedDraft(draftIndex, field, event.target.value)
                                    }
                                    className={fieldClass}
                                  >
                                    <option value="" className="bg-white text-gray-900">
                                      Empty
                                    </option>
                                    {hasInvalidStatusValue && (
                                      <option
                                        value={fieldValue}
                                        disabled
                                        className="bg-red-50 text-red-600"
                                      >
                                        {fieldValue}
                                      </option>
                                    )}
                                    {statusOptions.map((status) => (
                                      <option
                                        key={status}
                                        value={status}
                                        className="bg-white text-gray-900"
                                      >
                                        {status}
                                      </option>
                                    ))}
                                  </select>
                                ) : (
                                  <input
                                    ref={(element) => {
                                      failedFieldRefs.current[errorKey] = element;
                                    }}
                                    data-error-key={errorKey}
                                    type="text"
                                    inputMode={field === "mobileNumber" ? "tel" : undefined}
                                    maxLength={field === "mobileNumber" ? 21 : undefined}
                                    value={fieldValue}
                                    onChange={(event) => {
                                      const value =
                                        field === "mobileNumber"
                                          ? sanitizePhoneNumbers(event.target.value)
                                          : event.target.value;
                                      updateFailedDraft(draftIndex, field, value);
                                    }}
                                    className={fieldClass}
                                  />
                                )}
                                {error && (
                                  <span className="mt-1 block max-w-44 text-xs leading-4 text-red-600">
                                    {error}
                                  </span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
            {failedDrafts.length > FAILED_ROWS_PER_PAGE && (
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-4">
                <p className="text-sm text-gray-600">
                  Showing {(failedPreviewPage - 1) * FAILED_ROWS_PER_PAGE + 1}–
                  {Math.min(
                    failedPreviewPage * FAILED_ROWS_PER_PAGE,
                    failedDrafts.length,
                  )} of {failedDrafts.length} failed rows
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setFailedPreviewPage((page) => Math.max(1, page - 1))
                    }
                    disabled={failedPreviewPage === 1}
                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Previous
                  </button>
                  <span className="text-sm font-medium text-gray-700">
                    Page {failedPreviewPage} of{" "}
                    {Math.ceil(failedDrafts.length / FAILED_ROWS_PER_PAGE)}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setFailedPreviewPage((page) =>
                        Math.min(
                          Math.ceil(failedDrafts.length / FAILED_ROWS_PER_PAGE),
                          page + 1,
                        ),
                      )
                    }
                    disabled={
                      failedPreviewPage >=
                      Math.ceil(failedDrafts.length / FAILED_ROWS_PER_PAGE)
                    }
                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </Modal>

          <Modal
            open={!!editingRow}
            title="Edit Enquiry"
            onClose={closeEdit}
            footer={
              <>
                {canApproveEnquiry && !editingRow?.approved && (
                  <button
                    type="button"
                    onClick={() => {
                      const row = editingRow;
                      closeEdit();
                      approveEnquiry(row);
                    }}
                    disabled={!editingRow?.welcomeCallMessage}
                    title={editingRow?.welcomeCallMessage ? "Approve enquiry" : "Confirm Welcome Call/Message first"}
                    className="inline-flex items-center gap-2 rounded-lg border border-green-200 px-4 py-2 text-sm font-semibold text-green-700 transition-colors hover:bg-green-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-300"
                  >
                    <CheckCircle2 size={16} strokeWidth={2.2} />
                    Approve Enquiry
                  </button>
                )}
                {canTransferMerchant && (
                  <button
                    type="button"
                    onClick={openTransferMerchant}
                    disabled={savingEdit || transferModal.saving}
                    title="Transfer this enquiry to another merchant"
                    className="inline-flex items-center gap-2 rounded-lg border border-blue-200 px-4 py-2 text-sm font-semibold text-blue-700 transition-colors hover:bg-blue-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-300"
                  >
                    <ArrowRightLeft size={16} strokeWidth={2.2} />
                    Transfer Merchant
                  </button>
                )}
                {!isMerchantUser && canMoveEnquiry &&
                  (!sendBack || editingRow?.merchantResubmitted === true) && (
                    <button
                      type="button"
                      onClick={() => {
                        const row = {
                          ...editingRow,
                          merchantName: editForm.merchantName,
                          merchantIdentifier: editForm.merchantIdentifier,
                        };
                        closeEdit();
                        sendBackEnquiry(row);
                      }}
                      disabled={merchantIdentifierLoading || !String(editForm.merchantIdentifier || "").trim()}
                      title={
                        merchantIdentifierLoading
                          ? "Fetching merchant identifier"
                          : editForm.merchantIdentifier
                            ? "Send back to matched merchant"
                            : "Merchant identifier is required"
                      }
                      className="inline-flex items-center gap-2 rounded-lg border border-orange-200 px-4 py-2 text-sm font-semibold text-orange-700 transition-colors hover:bg-orange-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-300 disabled:hover:bg-white"
                    >
                      <SendToBack size={16} strokeWidth={2.2} />
                      Send Back
                    </button>
                  )}
                {isMerchantUser && editingRow?.isSendBack &&
                  editingRow?.merchantResubmitted !== true && (
                    <button
                      type="button"
                      onClick={() => {
                        const row = editingRow;
                        closeEdit();
                        sendBackToAdmin(row);
                      }}
                      className="inline-flex items-center gap-2 rounded-lg border border-orange-200 px-4 py-2 text-sm font-semibold text-orange-700 transition-colors hover:bg-orange-50"
                    >
                      <Send size={16} strokeWidth={2.2} />
                      Send To Admin
                    </button>
                  )}
                <button
                  type="button"
                  onClick={closeEdit}
                  disabled={savingEdit}
                  className="border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium px-4 py-2 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveEdit}
                  disabled={savingEdit}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
                >
                  {savingEdit ? "Saving..." : "Save Changes"}
                </button>
              </>
            }
          >
            <Modal
              open={transferModal.open}
              title="Transfer Merchant"
              maxWidth="max-w-lg"
              bodyClassName="overflow-visible"
              onClose={() => !transferModal.saving && closeTransferMerchant()}
              footer={
                <>
                  <button
                    type="button"
                    onClick={closeTransferMerchant}
                    disabled={transferModal.saving}
                    className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={confirmTransferMerchant}
                    disabled={
                      transferModal.saving ||
                      transferModal.loading ||
                      !String(transferModal.identifier || "").trim()
                    }
                    className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                  >
                    <ArrowRightLeft size={16} strokeWidth={2.2} />
                    {transferModal.saving ? "Transferring..." : "Transfer"}
                  </button>
                </>
              }
            >
              <div className="space-y-4">
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <p className="text-xs font-medium text-gray-500">Current Merchant</p>
                  <p className="mt-1 text-sm font-semibold text-gray-800">
                    {editForm.merchantName || "—"}
                  </p>
                  <p className="text-xs text-gray-500">
                    Identifier: {editForm.merchantIdentifier || "—"}
                  </p>
                </div>

                <div className="relative">
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Identifier
                  </label>
                  <input
                    type="text"
                    value={transferModal.identifier}
                    onChange={(event) => {
                      const value = event.target.value;
                      setTransferModal((current) => ({
                        ...current,
                        identifier: value,
                        suggestionsOpen: true,
                        selectedMerchant:
                          current.selectedMerchant?.identifier === value
                            ? current.selectedMerchant
                            : null,
                        error: "",
                      }));
                    }}
                    placeholder="Enter identifier number"
                    className={`${inputCls} w-full`}
                    autoFocus
                  />
                  {transferModal.suggestionsOpen &&
                    (transferModal.loading || transferModal.suggestions.length > 0) && (
                    <div className="absolute left-0 right-0 top-full z-[120] mt-1 max-h-64 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-2xl">
                      {transferModal.loading ? (
                        <p className="px-3 py-2 text-sm text-gray-500">
                          Searching merchants...
                        </p>
                      ) : (
                        transferModal.suggestions.map((merchant) => (
                          <button
                            key={merchant._id || merchant.identifier}
                            type="button"
                            onClick={() => selectTransferMerchant(merchant)}
                            className="block w-full px-3 py-2 text-left transition-colors hover:bg-blue-50"
                          >
                            <span className="block text-sm font-semibold text-gray-800">
                              {merchant.identifier}
                            </span>
                            <span className="block text-xs text-gray-500">
                              {merchant.name || "Unnamed merchant"}
                            </span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>

                {transferModal.selectedMerchant && (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                    <p className="text-xs font-medium text-blue-600">
                      Selected Merchant
                    </p>
                    <p className="mt-1 text-sm font-semibold text-blue-900">
                      {transferModal.selectedMerchant.name}
                    </p>
                    <p className="text-xs text-blue-700">
                      Identifier: {transferModal.selectedMerchant.identifier}
                    </p>
                  </div>
                )}

                {transferModal.error && (
                  <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
                    {transferModal.error}
                  </p>
                )}
              </div>
            </Modal>

            {isMerchantUser || sendBack ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {editInput("caseName", "Case Name *")}
                {editInput("mobileNumber", "Mobile Number")}
                {editInput("referredBy", "Ref.by")}
                {editInput("loanType", "Loan Type")}
                {editInput("amountRequired", "Amount Required", "number")}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {editInput("merchantName", "Merchant Name")}
                <div>
                  {editInput("merchantIdentifier", "Identifier", "text", true)}
                  {merchantIdentifierLoading && (
                    <p className="mt-1 text-xs text-gray-400">Fetching identifier...</p>
                  )}
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-gray-700">Lead Date</p>
                  <p className="text-sm text-gray-600">
                    {editingRow?.leadDate
                      ? dayjs(editingRow.leadDate).format("DD MMM YYYY")
                      : editingRow?.createdAt
                        ? dayjs(editingRow.createdAt).format("DD MMM YYYY")
                        : "—"}
                  </p>
                </div>
                {editInput("documentsDate", "Documents Date", "date")}
                {editInput("loginDate", "Login Date", "date")}
                {editInput("disburshMonth", "Disbursh Month", "month")}
                {editInput("caseName", "Case Name")}
                {editInput("mobileNumber", "Mobile Number")}
                {editInput("referredBy", "Ref.by")}
                {editInput("loanType", "Loan Type")}
                {editInput("amountRequired", "Amount Require", "number")}
                {editInput("loginFrom", "Login From")}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={editForm.status}
                    onChange={(e) => {
                      const val = e.target.value;
                      changeEdit("status", val);
                      if (val !== "Rejected") {
                        changeEdit("sendBackReason", "");
                        setRejectionReasonError(false);
                      }
                    }}
                    className={inputCls}
                  >
                    <option value="">Select</option>
                    {EDIT_STATUS_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
                {editForm.status === "Rejected" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Rejection Reason *
                    </label>
                    <textarea
                      rows={3}
                      required
                      value={editForm.sendBackReason}
                      onChange={(e) => {
                        changeEdit("sendBackReason", e.target.value);
                        if (e.target.value.trim()) setRejectionReasonError(false);
                      }}
                      placeholder="Enter rejection reason"
                      className={`${inputCls} ${
                        rejectionReasonError
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                          : ""
                      }`}
                    />
                    {rejectionReasonError && (
                      <p className="mt-1 text-xs font-medium text-red-600">
                        Rejection reason is required. Please enter a reason before saving.
                      </p>
                    )}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Workable / Notworkable
                  </label>
                  <select
                    value={editForm.workableStatus}
                    onChange={(e) =>
                      changeEdit("workableStatus", e.target.value)
                    }
                    className={inputCls}
                  >
                    <option value="">Select</option>
                    <option value="workable">Workable</option>
                    <option value="not_workable">Notworkable</option>
                  </select>
                </div>
                {editInput(
                  "feLoginAmount",
                  "Fe Login Amount",
                  "number",
                  !editingRow?.approved ||
                    editingRow?.paid === true ||
                    (() => {
                      const { due, paid } = getPaymentAmounts(editingRow);
                      return paid.login > 0 && paid.login >= due.login;
                    })(),
                )}
                {editInput(
                  "feDisbursementAmount",
                  "Fe Disburshment Amount",
                  "number",
                  !editingRow?.approved ||
                    editingRow?.paid === true ||
                    (() => {
                      const { due, paid } = getPaymentAmounts(editingRow);
                      return (
                        paid.disbursement > 0 &&
                        paid.disbursement >= due.disbursement
                      );
                    })(),
                )}
                {editInput("bankPayments", "Bank Payments", "number")}
                {editInput("loginFeesStatus", "Login Fees Status")}
              </div>
            )}

            {getTransferDetails(editingRow) && (
              <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-3">
                <p className="text-sm font-semibold text-blue-900">
                  Transfer Details
                </p>
                <div className="mt-2 grid grid-cols-1 gap-2 text-sm text-blue-800 sm:grid-cols-2 lg:grid-cols-4">
                  <p>
                    <span className="block text-xs font-medium text-blue-600">Transferred By</span>
                    {getTransferDetails(editingRow).by}
                    {getTransferDetails(editingRow).byIdentifier
                      ? ` (${getTransferDetails(editingRow).byIdentifier})`
                      : ""}
                  </p>
                  <p>
                    <span className="block text-xs font-medium text-blue-600">Transfer Time</span>
                    {getTransferDetails(editingRow).at}
                  </p>
                  <p>
                    <span className="block text-xs font-medium text-blue-600">From Merchant</span>
                    {getTransferDetails(editingRow).from}
                    {getTransferDetails(editingRow).fromIdentifier
                      ? ` (${getTransferDetails(editingRow).fromIdentifier})`
                      : ""}
                  </p>
                  <p>
                    <span className="block text-xs font-medium text-blue-600">Received By</span>
                    {getTransferDetails(editingRow).to}
                    {getTransferDetails(editingRow).toIdentifier
                      ? ` (${getTransferDetails(editingRow).toIdentifier})`
                      : ""}
                  </p>
                </div>
              </div>
            )}

            <div className="mt-4 max-w-sm">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Other Doc Type
              </label>
              <select
                value={editForm.additionalDocumentType}
                onChange={(e) =>
                  changeEdit("additionalDocumentType", e.target.value)
                }
                className={inputCls}
              >
                <option value="">Select</option>
                <option value="voter_id">Voter ID</option>
                <option value="passport">Passport</option>
                <option value="bill">Bill</option>
                <option value="driving_licence">Driving Licence</option>
              </select>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Remarks
              </label>
              <textarea
                rows={3}
                value={editForm.remarks}
                onChange={(e) => changeEdit("remarks", e.target.value)}
                className={inputCls}
              />
            </div>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {editDocumentInput("aadhaarImage", "Aadhaar Front")}
              {editDocumentInput("aadhaarBackImage", "Aadhaar Back")}
              {editDocumentInput("panCardImage", "PAN Card")}
              {editDocumentInput("bankStatementImage", "Bank Statement")}
              {editDocumentInput("additionalDocumentImage", "Other Document")}
            </div>
          </Modal>

          <div className="flex flex-col gap-3 mb-4 sm:mb-6 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-800">
              {title || (sendBack ? "Send Back Enquiries" : "Loan Enquiries")}
            </h1>
            <div className="grid grid-cols-1 gap-2 sm:flex sm:flex-wrap sm:items-center">
              {!sendBack && !isMerchantUser && (
                <>
                  <button
                    type="button"
                    onClick={exportExcel}
                    disabled={exporting}
                    className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
                  >
                    {exporting ? "Exporting..." : "Export Excel"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setImportOpen(true);
                      setSelectedImportFile(null);
                      setMessage("");
                      setUploadResult(null);
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
                  >
                    Import Excel
                  </button>
                </>
              )}
              {canDeleteEnquiry &&
                visibleColumns.some((column) => column.key === "select") && (
                  <button
                    onClick={deleteSelected}
                    disabled={!selectedIds.length || deleting}
                    className="bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
                  >
                    {deleting
                      ? "Deleting..."
                      : `Delete Selected${selectedIds.length ? ` (${selectedIds.length})` : ""}`}
                  </button>
                )}
            </div>
          </div>

          {message && (
            <div className="bg-white border border-gray-200 rounded-lg px-3 py-3 sm:px-4 text-sm text-gray-700 mb-4">
              {message}
            </div>
          )}

          <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4 mb-4 grid grid-cols-1 gap-3 sm:flex sm:flex-wrap sm:items-center min-w-0 overflow-visible">
            <input
              value={q}
              onChange={(e) => {
                setPage(1);
                setQ(e.target.value);
              }}
              placeholder="Search merchant / identifier / case / mobile / status / remarks"
              className={`${inputCls} w-full min-w-0 max-w-full sm:w-80`}
            />
            <InlineDropdown
              value={dateFilter}
              onChange={(nextValue) => {
                setPage(1);
                setDateFilter(nextValue);
              }}
              options={[
                { value: "all", label: "All Dates" },
                { value: "daily", label: "Daily" },
                { value: "weekly", label: "Weekly" },
                { value: "monthly", label: "Monthly" },
                { value: "custom", label: "Custom" },
              ]}
              ariaLabel="Filter enquiries by date"
            />
            {dateFilter === "custom" && (
              <>
                <input
                  type="date"
                  aria-label="Custom date from"
                  value={customDateFrom}
                  max={customDateTo || undefined}
                  onChange={(e) => {
                    setPage(1);
                    setCustomDateFrom(e.target.value);
                  }}
                  className={`${inputCls} w-full sm:w-auto`}
                />
                <input
                  type="date"
                  aria-label="Custom date to"
                  value={customDateTo}
                  min={customDateFrom || undefined}
                  onChange={(e) => {
                    setPage(1);
                    setCustomDateTo(e.target.value);
                  }}
                  className={`${inputCls} w-full sm:w-auto`}
                />
              </>
            )}
            <MonthMultiSelect
              value={selectedDisburshMonths}
              onChange={(months) => {
                setPage(1);
                setSelectedDisburshMonths(months);
              }}
            />
            <InlineDropdown
              value={statusFilter}
              onChange={(nextValue) => {
                setPage(1);
                setStatusFilter(nextValue);
              }}
              options={[
                { value: "all", label: "All Status" },
                ...EDIT_STATUS_OPTIONS.map((status) => ({
                  value: status,
                  label: status,
                })),
              ]}
              ariaLabel="Filter enquiries by status"
              widthClass="sm:w-48"
            />
            {!sendBack && !isMerchantUser && (
              <InlineDropdown
                value={sentBackFilter}
                onChange={(nextValue) => {
                  setPage(1);
                  setSentBackFilter(nextValue);
                }}
                options={[
                  { value: "all", label: "All Enquiries" },
                  { value: "sent-back", label: "Sent Back" },
                ]}
                ariaLabel="Filter sent back enquiries"
              />
            )}
            <InlineDropdown
              value={String(limit)}
              onChange={(nextValue) => {
                setPage(1);
                setLimit(Number(nextValue));
              }}
              options={[10, 20, 50].map((n) => ({
                value: String(n),
                label: `${n}/page`,
              }))}
              ariaLabel="Rows per page"
            />
          </div>

          <div className="mb-3 flex flex-wrap gap-2">
            <div className="flex items-center justify-between gap-4 rounded-lg border border-blue-100 bg-blue-50 px-3 py-1.5 sm:min-w-36">
              <p className="text-[11px] font-medium uppercase tracking-wide text-blue-600">
                Total Leads
              </p>
              <p className="text-lg font-bold leading-none text-blue-900">
                {loading ? "—" : data.total}
              </p>
            </div>
            <div className="flex items-center justify-between gap-4 rounded-lg border border-orange-100 bg-orange-50 px-3 py-1.5 sm:min-w-36">
              <p className="text-[11px] font-medium uppercase tracking-wide text-orange-600">
                Send Back
              </p>
              <p className="text-lg font-bold leading-none text-orange-900">
                {loading ? "—" : data.sentBackTotal}
              </p>
            </div>
            <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5">
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">
                Viewing
              </p>
              <p className="whitespace-nowrap text-xs font-semibold text-gray-800">
                {activeDateLabel}
              </p>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm overflow-hidden min-w-0 w-full">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    {visibleColumns.map((c) => (
                      <th
                        key={c.key}
                        className={`px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200 whitespace-nowrap ${
                          c.key === "actions"
                            ? "sticky right-0 z-20 border-l shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.2)]"
                            : ""
                        }`}
                      >
                        {c.key === "select" ? (
                          <input
                            type="checkbox"
                            checked={allVisibleSelected}
                            onChange={toggleAllVisible}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        ) : c.key === "serial" ? (
                          <button
                            type="button"
                            onClick={() =>
                              setSerialSort((current) =>
                                current === "asc" ? "desc" : "asc",
                              )
                            }
                            className="inline-flex items-center gap-1 hover:text-gray-800"
                            aria-label={`Sort serial number ${serialSort === "asc" ? "descending" : "ascending"}`}
                          >
                            {c.label}
                            <span aria-hidden="true">
                              {serialSort === "asc" ? "▲" : "▼"}
                            </span>
                          </button>
                        ) : (
                          c.label
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loading ? (
                    <tr>
                      <td
                        colSpan={visibleColumns.length}
                        className="px-4 py-12 text-center"
                      >
                        <div className="flex justify-center">
                          <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                        </div>
                      </td>
                    </tr>
                  ) : data.items.length === 0 ? (
                    <tr>
                      <td
                        colSpan={visibleColumns.length}
                        className="px-4 py-12 text-center text-gray-400 text-sm"
                      >
                        No records found
                      </td>
                    </tr>
                  ) : (
                    displayedItems.map((row, index) => (
                      <tr
                        key={row._id}
                        className="group hover:bg-gray-50 transition-colors"
                      >
                        {visibleColumns.map((col) => (
                          <td
                            key={col.key}
                            className={`px-4 py-3 text-sm text-gray-700 whitespace-nowrap ${
                              col.key === "actions"
                                ? "sticky right-0 z-10 border-l border-gray-100 bg-white shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.2)] group-hover:bg-gray-50"
                                : ""
                            }`}
                          >
                            {col.key === "select" ? (
                              <input
                                type="checkbox"
                                checked={selectedIds.includes(row._id)}
                                onChange={() => toggleOne(row._id)}
                                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                            ) : col.key === "welcomeCallMessage" ? (
                              canConfirmWelcomeCall ? (
                                <button
                                  type="button"
                                  onClick={() =>
                                    !row.welcomeCallMessage &&
                                    setWelcomeConfirmModal({ open: true, row })
                                  }
                                  disabled={row.welcomeCallMessage}
                                  title={
                                    row.welcomeCallMessage
                                      ? "Welcome Call/Message confirmed"
                                      : "Confirm Welcome Call/Message"
                                  }
                                  aria-label="Confirm Welcome Call/Message"
                                  className={`inline-flex h-7 w-7 items-center justify-center rounded-full border transition-colors ${
                                    row.welcomeCallMessage
                                      ? "cursor-default border-green-600 bg-green-600 text-white"
                                      : "border-green-300 bg-green-50 text-green-600 hover:border-green-500 hover:bg-green-100"
                                  }`}
                                >
                                  <CheckCircle2 size={15} strokeWidth={2.5} />
                                </button>
                              ) : (
                                row.welcomeCallMessage ? (
                                  <CheckCircle2
                                    size={17}
                                    className="text-green-600"
                                    aria-label="Confirmed"
                                  />
                                ) : (
                                  <span className="text-gray-300">—</span>
                                )
                              )
                            ) : col.key === "actions" ? (
                              <div className="flex items-center gap-2">
                                {canEditRow(row) && (
                                  <button
                                    type="button"
                                    onClick={() => openEdit(row)}
                                    title="Edit enquiry"
                                    aria-label="Edit enquiry"
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-blue-200 text-blue-600 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-800 transition-colors"
                                  >
                                    <Pencil size={15} strokeWidth={2.2} />
                                  </button>
                                )}
                                {canApproveEnquiry &&
                                  row.approved &&
                                  !row.paid &&
                                  getPaymentAmounts(row).outstandingTotal > 0 && (
                                  <button
                                    type="button"
                                    onClick={() => initiatePayment(row)}
                                    title="Initiate payment"
                                    aria-label="Initiate payment"
                                    className="inline-flex h-8 items-center justify-center gap-1.5 rounded-lg border border-purple-200 bg-white px-2.5 text-xs font-semibold text-purple-700 transition-colors hover:border-purple-400 hover:bg-purple-50 hover:text-purple-800"
                                  >
                                    <CreditCard size={15} strokeWidth={2.2} />
                                    <span>Payment Initiate</span>
                                  </button>
                                )}
                                {sendBack && canRestoreEnquiry && (
                                  <button
                                    type="button"
                                    onClick={() => restoreEnquiry(row)}
                                    title="Restore enquiry"
                                    aria-label="Restore enquiry"
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-green-200 text-green-600 hover:border-green-400 hover:bg-green-50 hover:text-green-800 transition-colors"
                                  >
                                    <RotateCcw size={15} strokeWidth={2.2} />
                                  </button>
                                )}
                                {canDeleteEnquiry && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSelectedIds([row._id]);
                                      setDeleteConfirmOpen(true);
                                    }}
                                    title="Delete enquiry"
                                    aria-label="Delete enquiry"
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-red-200 text-red-600 hover:border-red-400 hover:bg-red-50 hover:text-red-800 transition-colors"
                                  >
                                    <Trash2 size={15} strokeWidth={2.2} />
                                  </button>
                                )}
                              </div>
                            ) : (
                              <span
                                className={
                                  col.key === "remarks"
                                    ? "block max-w-[220px] truncate"
                                    : ""
                                }
                                title={
                                  row[col.key] === undefined || row[col.key] === null
                                    ? ""
                                    : String(row[col.key])
                                }
                              >
                                {renderCell(
                                  row,
                                  col,
                                  index,
                                  page,
                                  limit,
                                  (ledgerRow) =>
                                    setLedgerModal({ open: true, row: ledgerRow }),
                                  serialSort,
                                  data.items.length,
                                )}
                              </span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between gap-2 px-3 py-3 sm:px-4 border-t border-gray-200">
              <span className="text-sm text-gray-500">
                Page {page} of {Math.max(1, data.pages)}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  Prev
                </button>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= Math.max(1, data.pages)}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
          <Modal
            open={welcomeConfirmModal.open}
            title="Confirm Welcome Call/Message"
            maxWidth="max-w-md"
            onClose={() => setWelcomeConfirmModal({ open: false, row: null })}
            footer={
              <>
                <button
                  type="button"
                  onClick={() => setWelcomeConfirmModal({ open: false, row: null })}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmWelcomeCallMessage}
                  className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-green-700"
                >
                  <CheckCircle2 size={16} strokeWidth={2.3} />
                  Confirm
                </button>
              </>
            }
          >
            <div className="flex items-start gap-3 rounded-xl border border-green-100 bg-green-50 p-4">
              <CheckCircle2 className="mt-0.5 shrink-0 text-green-600" size={22} />
              <p className="text-sm text-gray-700">
                Confirm that the Welcome Call/Message has been completed for{" "}
                <span className="font-semibold text-gray-900">
                  {welcomeConfirmModal.row?.caseName || "this enquiry"}
                </span>
                ?
              </p>
            </div>
          </Modal>
          <Modal
            open={approveModal.open}
            title="Approve Enquiry"
            maxWidth="max-w-md"
            onClose={() => setApproveModal({ open: false, row: null })}
            footer={
              <>
                <button
                  type="button"
                  onClick={() => setApproveModal({ open: false, row: null })}
                  className="border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium px-4 py-2 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmApproveEnquiry}
                  className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
                >
                  <CheckCircle2 size={16} strokeWidth={2.2} />
                  Approve
                </button>
              </>
            }
          >
            <div className="flex items-start gap-3">
              <div className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green-50 text-green-700 border border-green-100">
                <CheckCircle2 size={20} strokeWidth={2.2} />
              </div>
              <div className="min-w-0">
                <p className="text-sm text-gray-600">
                  Approve enquiry for{" "}
                  <span className="font-semibold text-gray-900">
                    {approveModal.row?.caseName || "this case"}
                  </span>{" "}
                  and add it to the merchant account?
                </p>
                <p className="mt-2 text-xs text-gray-500">
                  This action marks the enquiry as approved and credits the merchant wallet.
                </p>
              </div>
            </div>
          </Modal>
          <Modal
            open={ledgerModal.open}
            title="Payment Ledger"
            maxWidth="max-w-2xl"
            onClose={() => setLedgerModal({ open: false, row: null })}
            footer={
              <button
                type="button"
                onClick={() => setLedgerModal({ open: false, row: null })}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
              >
                Close
              </button>
            }
          >
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                <p className="text-sm font-semibold text-gray-900">
                  {ledgerModal.row?.caseName || "Enquiry"}
                </p>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-gray-500">
                    Paid{" "}
                    <span className="font-bold text-green-700">
                      {formatPaymentAmount(ledgerModal.row?.paidAmount)}
                    </span>
                  </span>
                  <span className="text-gray-500">
                    Outstanding{" "}
                    <span className="font-bold text-orange-700">
                      {formatPaymentAmount(selectedLedgerOutstanding)}
                    </span>
                  </span>
                </div>
              </div>

              <div className="max-h-[420px] overflow-y-auto rounded-lg border border-gray-200">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                    <tr className="border-b border-gray-200">
                      <th className="px-3 py-2 text-left font-semibold">#</th>
                      <th className="px-3 py-2 text-left font-semibold">Date</th>
                      <th className="px-3 py-2 text-right font-semibold">Lead</th>
                      <th className="px-3 py-2 text-right font-semibold">Login</th>
                      <th className="px-3 py-2 text-right font-semibold">Disburse</th>
                      <th className="px-3 py-2 text-right font-semibold">Total</th>
                      <th className="px-3 py-2 text-center font-semibold">Proof</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {selectedLedger.map((entry, ledgerIndex) => (
                      <tr
                        key={`${entry.paidAt || "payment"}-${ledgerIndex}`}
                        className="hover:bg-gray-50"
                      >
                        <td className="px-3 py-2 text-gray-500">
                          {ledgerIndex + 1}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-gray-600">
                          {entry.paidAt
                            ? dayjs(entry.paidAt).format("DD MMM YY, hh:mm A")
                            : "—"}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-800">
                          {formatPaymentAmount(entry.leadAmount)}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-800">
                          {formatPaymentAmount(entry.loginAmount)}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-800">
                          {formatPaymentAmount(entry.disbursementAmount)}
                        </td>
                        <td className="px-3 py-2 text-right font-bold text-green-700">
                          {formatPaymentAmount(entry.totalAmount)}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {entry.proofImage ? (
                            <a
                              href={getDocumentUrl(entry.proofImage)}
                              target="_blank"
                              rel="noreferrer"
                              className="font-semibold text-blue-600 hover:text-blue-800"
                            >
                              View
                            </a>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Modal>
          <Modal
            open={paymentModal.open}
            title="Initiate Payment"
            maxWidth="max-w-md"
            onClose={() =>
              setPaymentModal({
                open: false,
                row: null,
                file: null,
                amounts: { lead: "", login: "", disbursement: "" },
              })
            }
            footer={
              <>
                <button
                  type="button"
                  onClick={() =>
                    setPaymentModal({
                      open: false,
                      row: null,
                      file: null,
                      amounts: { lead: "", login: "", disbursement: "" },
                    })
                  }
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmInitiatePayment}
                  disabled={
                    !paymentModal.file ||
                    !paymentAmountsValid ||
                    paymentTotal <= 0 ||
                    paymentSubmitting
                  }
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-purple-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                >
                  <CreditCard size={16} strokeWidth={2.2} />
                  {paymentSubmitting ? "Processing..." : "Initiate Payment"}
                </button>
              </>
            }
          >
            <div className="rounded-xl border border-purple-100 bg-purple-50/40 p-4">
              <p className="mb-4 text-sm text-gray-600">
                Payment details for{" "}
                <span className="font-semibold text-gray-900">
                  {paymentRow?.caseName || "this case"}
                </span>
              </p>
              <div className="space-y-3">
                {[
                  ["lead", "Lead Amount", paymentAmounts.lead],
                  ["login", "Login Amount", paymentAmounts.login],
                  ["disbursement", "Disburse Amount", paymentAmounts.disbursement],
                ].map(([key, label, maximum]) => (
                  <div key={key}>
                    <label className="mb-1 block text-xs font-semibold text-gray-600">
                      {label}{" "}
                      <span
                        className={`font-normal ${
                          maximum <= 0 &&
                          paymentDetails.due[key] > 0 &&
                          paymentDetails.paid[key] >= paymentDetails.due[key]
                            ? "text-green-600"
                            : "text-gray-400"
                        }`}
                      >
                        {maximum <= 0
                          ? paymentDetails.due[key] > 0 &&
                            paymentDetails.paid[key] >= paymentDetails.due[key]
                            ? "(Paid)"
                            : "(Awaiting amount)"
                          : `(Outstanding ${formatPaymentAmount(maximum)})`}
                      </span>
                    </label>
                    <input
                      type="number"
                      min="0"
                      max={maximum}
                      step="0.01"
                      disabled={maximum <= 0}
                      readOnly
                      value={paymentModal.amounts?.[key] ?? ""}
                      className="w-full cursor-default rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-800 outline-none disabled:bg-green-50 disabled:text-green-700"
                    />
                  </div>
                ))}
                <div className="flex items-center justify-between border-t border-purple-200 pt-3">
                  <span className="text-sm font-semibold text-gray-700">Payment Total</span>
                  <span className="text-lg font-bold text-purple-700">
                    {formatPaymentAmount(paymentTotal)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs text-gray-600">
                  <span>Outstanding after payment</span>
                  <span className="font-semibold text-orange-700">
                    {formatPaymentAmount(paymentBalanceAfter)}
                  </span>
                </div>
                <div className="border-t border-purple-200 pt-3">
                  <label className="mb-1 block text-xs font-semibold text-gray-700">
                    Payment Proof Photo <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(event) =>
                      setPaymentModal((current) => ({
                        ...current,
                        file: event.target.files?.[0] || null,
                      }))
                    }
                    className="block w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 file:mr-3 file:rounded-md file:border-0 file:bg-purple-100 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-purple-700 hover:file:bg-purple-200"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Upload a JPG, PNG, or other image up to 5 MB.
                  </p>
                  {!paymentModal.file && (
                    <p className="mt-1 text-xs font-medium text-orange-600">
                      Choose a payment photo to enable the Initiate Payment button.
                    </p>
                  )}
                  {paymentModal.error && (
                    <p className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700">
                      {paymentModal.error}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </Modal>
          <Modal
            open={sendBackModal.open}
            title={sendBackModal.mode === "admin" ? "Send To Admin" : "Send Back Enquiry"}
            maxWidth="max-w-md"
            onClose={() => setSendBackModal({ open: false, row: null, mode: "" })}
            footer={
              <>
                <button
                  type="button"
                  onClick={() => setSendBackModal({ open: false, row: null, mode: "" })}
                  className="border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium px-4 py-2 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={
                    sendBackModal.mode === "admin"
                      ? confirmSendBackToAdmin
                      : confirmSendBackEnquiry
                  }
                  className="inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
                >
                  {sendBackModal.mode === "admin" ? (
                    <Send size={16} strokeWidth={2.2} />
                  ) : (
                    <SendToBack size={16} strokeWidth={2.2} />
                  )}
                  {sendBackModal.mode === "admin" ? "Send To Admin" : "Send Back"}
                </button>
              </>
            }
          >
            <div className="flex items-start gap-3">
              <div className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-orange-50 text-orange-700 border border-orange-100">
                {sendBackModal.mode === "admin" ? (
                  <Send size={20} strokeWidth={2.2} />
                ) : (
                  <SendToBack size={20} strokeWidth={2.2} />
                )}
              </div>
              <div className="min-w-0">
                <p className="text-sm text-gray-600">
                  {sendBackModal.mode === "admin"
                    ? "Send updated enquiry for"
                    : "Send back enquiry for"}{" "}
                  <span className="font-semibold text-gray-900">
                    {sendBackModal.row?.caseName || "this case"}
                  </span>
                  {sendBackModal.mode === "admin"
                    ? " to the Loan Enquiries page?"
                    : " to the merchant?"}
                </p>
                <p className="mt-2 text-xs text-gray-500">
                  {sendBackModal.mode === "admin"
                    ? "The admin will see this updated enquiry again in Loan Enquiries."
                    : "The merchant can edit the enquiry and send it back to admin."}
                </p>
                {sendBackModal.mode !== "admin" && (
                  <div className="mt-2 space-y-1 text-xs font-medium text-gray-700">
                    <p>
                      Merchant:{" "}
                      <span className="font-semibold text-gray-900">
                        {sendBackModal.row?.merchantName ||
                          sendBackModal.row?.merchant?.name ||
                          "—"}
                      </span>
                    </p>
                    <p>
                      Identifier:{" "}
                      <span className="font-semibold text-gray-900">
                        {sendBackModal.row?.merchantIdentifier ||
                          sendBackModal.row?.merchant?.identifier ||
                          "—"}
                      </span>
                    </p>
                  </div>
                )}
              </div>
            </div>
          </Modal>
          <Modal
  open={rejectModal.open}
  title="Reject Enquiry"
  onClose={() => setRejectModal({ open: false, row: null, reason: "" })}
  footer={
    <>
      <button
        type="button"
        onClick={() => setRejectModal({ open: false, row: null, reason: "" })}
        className="border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium px-4 py-2 rounded-lg transition-colors"
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={submitReject}
        disabled={!rejectModal.reason.trim()}
        className="bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
      >
        Confirm Reject
      </button>
    </>
  }
>
  <div className="space-y-3">
    <p className="text-sm text-gray-600">
      Please provide a reason for rejecting{" "}
      <span className="font-semibold text-gray-800">{rejectModal.row?.caseName || "this enquiry"}</span>.
    </p>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Reason <span className="text-red-500">*</span>
      </label>
      <textarea
        rows={3}
        value={rejectModal.reason}
        onChange={e => setRejectModal(prev => ({ ...prev, reason: e.target.value }))}
        placeholder="Enter rejection reason..."
        className={`${inputCls} w-full`}
        autoFocus
      />
    </div>
  </div>
</Modal>
        </>
      )}
    </div>
  );
}
